import {defineComponent} from 'vue';
import i18nGlobal from '@/i18ns/i18n';
const i18n = i18nGlobal.global;
import {MSConstant} from '@/commons/wildcards/index.js';
import {useContextStore} from '@/stores/contextStore';
import _ from 'lodash';
import moment from 'moment';
import {showError, showAlert} from '@/commons/globalMessage';
import {useRoute, useRouter} from 'vue-router';
import i18nEnum from '@/i18ns/vi/branch/igov/i18nEnum.js';
import popupUtil from '@/commons/popupUtil.js';
import ModelState from '@/commons/wildcards/constants/ModelState';
import {useDbOptionStore} from '@/stores/dbOptionStore';
import fileApi from '@/apis/fl/fileApi';
import StorageFileType from '@/commons/wildcards/constants/StorageFileType';
import EventBusGlobal, {GlobalEventName} from '@/commons/eventBusGlobal';
import {msFormat} from './msFormat';
import IndexedDB from '@/commons/indexedDB';
import WebSocketEvent from '@/commons/wildcards/constants/WebSocketEvent';
import msDateUtil from '@/components/datepicker/msDateUtil';
import {useToast} from 'vue-toast-notification';

/**
 * Class chứa các function dùng chung cho hệ thống phía client
 */
class CommonFunction {
  countMark = 0;

  getMainLoading() {
    const el = document.body.querySelector('#mloading');
    return el;
  }
  mask(important) {
    important && this.countMark++;
    const el = this.getMainLoading();
    if (el) {
      el.style.display = 'flex';
    }
  }
  unmask(important) {
    important && this.countMark--;

    if (!this.countMark) {
      const el = this.getMainLoading();
      if (el) {
        el.style.display = 'none';
      }
    }
  }
  infoLogging() {}
  errorLogging() {}
  handleException(error, showAlert) {
    console.error(error);

    if (showAlert) {
      const localize = i18n;
      showError(localize.t('i18nCommon.GlobalErrorMessage'));
    }
  }

  /**
   * Xử lý hiển thị dialog
   * @author DNThang - 30.10.2019
   * DNThang - 06.05.2020: Trong trường hợp owner là popup thì sẽ append vào content để không bị chìm ở dưới
   */
  prepareForDialog(component, owner, options) {
    if (owner && component) {
      const frmType = defineComponent({extends: component});
      const frm = new frmType(options);

      frm.$mount();

      if (owner.isMsPopup) {
        const contentElement = owner.$refs.popupx; // owner.$el.querySelector('.ms-popup-content.ms-popup');
        contentElement.appendChild(frm.$el);
      } else {
        if (owner instanceof Element) {
          owner.appendChild(frm.$el);
        } else {
          owner.$el.appendChild(frm.$el);
        }
      }

      return frm;
    }

    return;
  }
  /**
   * Build vue key cho data
   */
  genVKeyValue(data, field = '__vKeyValue') {
    if (data) {
      data.forEach(item => {
        item[field] = this.generateUUID();
      });
    }
  }
  /**
   *  Tạo uuid
   *  Create by: pvduong1 - 30/08/2019
   */
  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      let d = new Date().getTime();
      d += performance.now();
      const r = (d + Math.random() * 16) % 16 | 0;
      d = Math.floor(d / 16);
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }
  /**
   * Guild empty
   */
  generateEmptyUUID() {
    return '00000000-0000-0000-0000-000000000000';
  }
  /**
   * Kiểm tra 2 đối tượng có phải là cha con của nhau không
   */
  hasParent(child, parent) {
    if (child.parentElement) {
      if (child.parentElement === parent) {
        return true;
      } else {
        return this.hasParent(child.parentElement, parent);
      }
    }

    return false;
  }

  /**
   * Kiểm tra đối tượng có class style không
   * Modified nntam: thêm case cối vào cho không chết
   */
  hasClass(element, className) {
    if (element && element.className) {
      return element.className.indexOf(className) > -1;
    }
    return false;
  }

  shortkeyPushView(el) {
    if (!window.shortkeyView) {
      window.shortkeyView = [];
    }
    // Sửa lỗi: Mảng JavaScript không có phương thức 'remove'
    // Thay thế bằng cách lọc mảng để loại bỏ el (nếu tồn tại)
    if (window.shortkeyView.length > 0) {
      window.shortkeyView = window.shortkeyView.filter(item => item !== el);
    }
    window.shortkeyView.push(el);
  }

  shortkeyPopView(el) {
    if (window.shortkeyView.length > 0) {
      window.shortkeyView = window.shortkeyView.filter(item => item !== el);
    }
  }

  shortkeyPopClass(target) {
    const el = document.querySelector(target);
    el && window.shortkeyView.remove(el);
  }

  /**
   * Xử lý render element động
   * modified by ntphong 28/5/2021 - isAppendTarget: nếu là append 1 đoạn code html vào 1 trong target
   */
  renderDynamicElement(target, component, owner, options, isAppendTarget) {
    if (component) {
      const frmType = defineComponent({extends: component});
      const frm = new frmType(options);

      frm.$mount();

      if (target && isAppendTarget && frm.$el) {
        target.appendChild(frm.$el);
      }
      return frm;
    }
  }

  /**
   * focus vào control tiếp theo
   */
  focusNextControl(curfocus) {
    const el = this.getNextInputControl(curfocus);
    if (el) {
      el.focus();
      if (typeof el.select === 'function') {
        el.select();
      }

      return true;
    }
  }

  /**
   * Kiểm tra element có hiển thị không
   * (modified) dtnga3 (04.10.2022) Đối với element có position :fixed thì phải check bằng thuộc tính display
   */
  isVisible(element) {
    if (window.getComputedStyle(element).position === 'fixed') {
      return window.getComputedStyle(element).display != 'none';
    } else {
      return element.offsetParent;
    }
  }

  /*
   * Lấy control nhập liệu tiếp theo
   */
  getNextInputControl(current) {
    const me = this;
    // i = c.index(),
    const p = current.parentNode;
    if (!p) {
      return null;
    }

    const ch = p.children;
    let r = null;
    let o;
    let i = 0;
    for (; i < ch.length; i++) {
      if (ch[i] === current) {
        break;
      }
    }

    for (let k = i + 1; k < ch.length; k++) {
      o = ch[k];
      if (
        ['INPUT', 'TEXTARE', 'BUTTON', 'A'].indexOf(o.tagName) > -1 &&
        !o.getAttribute('disabled') &&
        !o.getAttribute('tabindex') !== -1 &&
        me.isVisible(o)
      ) {
        r = o;
      } else {
        r = me.getFirstControlFocus(o);
      }

      if (r) {
        break;
      }
    }

    if (!r) {
      r = me.getNextInputControl(p);
    }

    return r;
  }
  /**
   * Lấy control nhập liệu đầu tiên
   * bổ sung thêm class BUG-100636 nhhoang1 18.06.2024
   */
  getFirstControlFocus(parent) {
    const me = this;
    const obj = parent || document;
    const selector = [
      'input:not([disabled]):not([tabindex="-1"]):not(.cancel-tab)',
      'textarea:not([disabled]):not([tabindex="-1"])',
      'select:not([disabled]):not([tabindex="-1"])',
      'button:not([disabled]):not([tabindex="-1"]):not(.ms-con-dropdown)',
      'a:not([disabled]):not([tabindex="-1"]):not(.ms-collapse-item--header):not(.dvc-file-link):not(.voucher-reference-link)',
    ].join(',');

    const items = obj.querySelectorAll(selector);
    if (items != null && items.length > 0) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (me.isVisible(item)) {
          return item;
        }
      }
    }

    return null;
  }

  /**
   * Tìm ra control Input đầu tiên theo scope truyền vào và cho phép sửa
   * @param {Element} scope Parent cần tìm input
   */
  findFirstControlInput(scope, query) {
    if (scope && scope.querySelector) {
      const input = scope.querySelector(
        query ||
          "input:not([type=button]):not([type=radio]):not([type=checkbox]):not([readonly]):not([disabled]):not([class='hidden']),textarea:not([readonly]):not([disabled]):not([class='hidden'])",
      );
      return input;
    }
    return;
  }

  /**
   * Tự động focus vào control nhập liệu đầu tiên theo scope truyền vào
   * @param {Element} scope Phạm vi cần xử lý focus
   * @author DNThang - 31.10.2019
   */
  focusFirstControlInput(scope, select = true, query) {
    const me = this;

    // setTimeout(() => {
    const input = me.findFirstControlInput(scope, query);
    if (input) {
      input.focus();

      if (select && typeof input.select === 'function') {
        input.select();
      }
    } else {
      // nếu k có input nào để focus thì focus vào dòng đầu tiên của grid( nếu có ) để có thể thực hiện phím tắt keydown up/down để chọn dòng
      me.focusFirstRowGrid(scope);
    }
    // });
  }

  /**
   * Tự động focus vào control nhập liệu đầu tiên theo scope truyền vào
   * @param {Element} scope Phạm vi cần xử lý focus
   * @author bnduc - 15.11.2019
   */
  focusFirstControl(scope) {
    if (scope) {
      const me = this;

      // setTimeout(() => {
      let input;
      if (
        scope.type === 'text' ||
        (scope.hasAttribute && scope.hasAttribute('type') && scope.getAttribute('type') === 'text')
      ) {
        input = scope;
      } else {
        input = me.findFirstControlFocus(scope);
      }

      if (input) {
        input.focus();
      }
      // });
    }
  }

  /**
   * Tự động focus vào control nhập liệu vào dòng đầu tiên của grid( nếu có ) để có thể thực hiện phím tắt keydown up/down để chọn dòng
   * @param {Element} scope Phạm vi cần xử lý focus
   * @author nnlam - 16.06.2021
   */
  focusFirstRowGrid(scope) {
    if (scope) {
      const me = this;

      // setTimeout(() => {
      let grid;
      if (me.grid) {
        grid = me.grid.$el;
      } else {
        grid = me.findFirstGrid(scope);
      }

      if (grid) {
        // me.$nextTick(() => {
        if (grid) {
          const trFirst = grid.querySelector('.ms-tbody.data tr');
          if (trFirst) {
            trFirst.focus();
          }
        }
      }
    }
  }

  /**
   * Tìm ra control Input đầu tiên theo scope truyền vào và cho phép sửa
   * @param {Element} scope Parent cần tìm input
   * @author bnduc - 15.11.2019
   */
  findFirstControlFocus(scope) {
    if (scope && scope.querySelector) {
      const input = scope.querySelector('input:not([readonly]):not([disabled]),button:not([disable]),a,textarea');
      return input;
    }
    return;
  }

  /**
   * focus vào control trước đó
   */
  focusPreviewControl(curfocus) {
    const me = this;
    const fn = function (parent) {
      const items = parent.querySelectorAll('input:not([disabled]),button:not([disable]),a');
      if (items[0] !== curfocus) {
        for (let i = 1; i < items.length; i++) {
          if (items[i] === curfocus) {
            for (let j = i - 1; j >= 0; j--) {
              // check item visible
              if (me.isVisible(items[j])) {
                items[j].focus();
                return;
              }
            }
            // reset focus to parent
            curfocus = parent;
          }
        }
      }
      fn(parent.parentElement);
    };

    fn(curfocus.parentElement);
  }

  /**
   * Tìm ra grid đầu tiên của form
   * @param {Element} scope Parent cần tìm grid
   * @author nnlam - 16.06.2021
   */
  findFirstGrid(scope) {
    if (scope && scope.querySelector) {
      const grid = scope.querySelector('.ms-grid-viewer');
      return grid;
    }
    return;
  }

  /**
   * Xử lý khi focus control cuối cùng thì sẽ thực thi fn
   */
  processUnfocusLastControl(e, fn) {
    if (e.which === 9 && !e.shiftKey) {
      // console.log(e.target.attributes.class.value);
      const cur = e.target;
      const els = e.currentTarget.querySelectorAll('*');
      let flag = true;
      for (let i = 0; i < els.length; i++) {
        if (els[i] === cur) {
          flag = false;
          continue;
        }
        if (flag) {
          continue;
        }

        els[i].focus();
        if (els[i] === document.activeElement) {
          e.preventDefault();
          return;
        }
      }

      // focus first control
      e.preventDefault();
      fn();
    }
  }

  /**
   * Kiểm tra 2 đôi tượng có khác nhau không
   */
  checkDiff(newVal, oldVal) {
    const ret = false;
    const newStr = JSON.stringify(newVal).replace(/:null,/g, ":'',");
    const oldStr = JSON.stringify(oldVal).replace(/:null,/g, ":'',");
    return newStr !== oldStr;

    // if (typeof newVal == "object" && typeof oldVal == "object") {
    //   for (let field in newVal) {
    //     if (typeof newVal[field] != "object" && newVal[field] != oldVal[field]) {
    //       ret = true;
    //       break;
    //     }
    //   }
    // }
    // return ret;
  }
  /**
   * pvduy 28/06/2021
   * lẩy ra danh sách các trường thay đổi và giá trị thay đổi
   * thêm đoạn xử lý trường hợp tìm sự thay đổi cửa giá trị datetime
   */
  getDiffListProperty(newVal, oldVal) {
    const listField = [];
    if (typeof newVal == 'object' && typeof oldVal == 'object') {
      for (const field in newVal) {
        if (typeof newVal[field] != 'object' && newVal[field] != oldVal[field]) {
          listField.push(field);
        } else if (newVal[field] instanceof Date) {
          let isChange = false;
          // nếu 2 giá trị khác nhau hoặc một trong 2 thằng null thì xác nhận sự thay đổi.
          if (
            newVal[field] != null &&
            oldVal[field] != null &&
            typeof newVal[field].getTime == 'function' &&
            typeof oldVal[field].getTime == 'function' &&
            newVal[field].getTime() != oldVal[field].getTime()
          ) {
            isChange = true;
          } else if (
            (newVal[field] == null && oldVal[field] != null) ||
            (newVal[field] == null && oldVal[field] != null)
          ) {
            isChange = true;
          }
          if (isChange) {
            listField.push(field);
          }
        }
      }
    }
    return listField;
  }

  // Chuyển dữ liệu thành dạng tree
  // Created by LTDAT(16.06.2020)
  // Collap hết nếu số dòng quá lớn >200 : NCPHUOC 20/05/2020
  nomalizeDataTree(data, idField, parentField) {
    const me = this;
    const dataTemp = [...data];
    const parentNodes = [];

    const cacheList = new Map();

    // Cache lại item trước để tăng performance
    dataTemp.forEach(item => {
      item.childNodes = [];
      cacheList.set(item[idField], item);
      delete item.isParent;
    });
    // const collapseAll = data.length > MSConstant.DefaultMaxRecord.GridTree;
    dataTemp.forEach((item, index) => {
      if (item[parentField]) {
        // Tìm cha
        const parent = cacheList.get(item[parentField]);

        if (parent) {
          if (!parent.hasOwnProperty('isParent')) {
            parent['isParent'] = true;
          }
          parent.childNodes.push(item);
          // Cache lại parentNode luôn
          item.parentNode = parent;
          // if (collapseAll && item.level === 2) {
          //   item.isHide = true;
          // }
        } else {
          parentNodes.push(item);
        }
      } else {
        // NMSINH: thêm món này vì nếu chọn thằng ko có Cha trong list thì nó chính là cha luôn
        parentNodes.push(item);
      }
      item.expanded = true;

      // if (collapseAll && item.level === 2) {
      //   item.expanded = false;
      // } else {
      //
      // }
    });
    return parentNodes;
  }

  /**
   * Thực hiện ghép các node cha đi kèm với các node con luôn để thực hiện show ra ngay bên dưới node cha
   * @param {[TreeModel]} dataResoures Danh sách các node cha cần sắp xếp
   * @param {*} expanded Có Mở rộng ra không
   * @param {*} level
   */
  getBodyData(dataResoures, expanded, level = 1, childNodes = 'childNodes', isGroup = false, customTitleGroup = null) {
    if (!dataResoures) {
      return [];
    }
    const data = [...dataResoures];
    let bodyData = [];
    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      const children = row[childNodes];
      const childrenLen = Object.prototype.toString.call(children).slice(8, -1) === 'Array' ? children.length : 0;

      row.level = level;
      // Vue.set(row, "isHide", false);
      // Vue.set(row, "expanded", expanded);
      // row.isHide = false;
      // row.expanded = expanded;

      row.isSelected = false;
      row._isSelectedMultiple = false;
      // Thêm caption group
      if (isGroup && row.caption) {
        if (!customTitleGroup) {
          row.caption += ` (${childrenLen} bản ghi)`;
        } else {
          if (typeof customTitleGroup === 'function') {
            row.caption = customTitleGroup(row);
          }
        }
      }
      bodyData.push(row);
      if (childrenLen > 0) {
        row._expandable = true;
        bodyData = bodyData.concat(this.getBodyData(children, expanded, level + 1, childNodes, isGroup));
      }
    }
    return bodyData;
  }

  /**
   * Sắp xếp dữ liệu cây để con đứng ngay sau cha
   * @param {*} orderField : Sắp xếp các con cùng cấp trước sau
   */
  sortTree(data, orderby, idField, parentIdField) {
    const sort = data.sortObject(orderby);

    const res = [];
    const parents = {};
    // đẩy root vào
    for (let i = 0; i < sort.length; i++) {
      const item = sort[i];
      const parentId = item[parentIdField];
      if (parentId === null || parentId === undefined) {
        res.push(item);
      }

      parents[item[idField]] = item;
    }

    // duyệt đẩy con
    // do push vào con lần lượt vào ngay sau cha -> đảo ngược lại để đúng thứ tự
    sort.reverse();
    for (let i = 0; i < res.length; i++) {
      const processItem = res[i];

      for (let j = 0; j < sort.length; j++) {
        const item = sort[j];
        if (res.indexOf(item) > -1) {
          // đã duyệt
          continue;
        }

        if (item[parentIdField] === processItem[idField]) {
          const index = res.indexOf(processItem);
          // append vào vị trí sau cha
          res.splice(index + 1, 0, item);
        }
      }
    }

    // dí nốt các item chưa xử lý (có cha nhưng k tìm thấy cha)
    if (res.length < sort.length) {
      for (let j = 0; j < sort.length; j++) {
        const item = sort[j];
        if (res.indexOf(item) === -1) {
          res.push(item);
        }
      }
    }

    return res;
  }

  /**
   * Lấy giá trị enum theo text
   */
  getEnumValue(enumName, text) {
    const source = this.getEnumSource(enumName);
    const textTrim = text.trim();
    for (let i = 0; i < source.length; i++) {
      const item = source[i];
      if (textTrim === item.enumText) {
        return item.enumValue;
      }
    }

    return null;
  }

  /**
   * Lấy giá trị enum text theo enum value
   */
  getEnumValueText(enumName, value) {
    const source = this.getEnumSource(enumName);
    for (let i = 0; i < source.length; i++) {
      const item = source[i];
      if (value === item.enumValue) {
        return item.enumText;
      }
    }

    return null;
  }

  /**
   * Lấy ra giá trị key của enum theo value
   * @param {Number} value Giá trị của enum cần lấy ra enum text
   * @param {String} enumName EnumName để get text
   */
  getEnumText(value, enumName) {
    // HTHIEP 14.03.2022: Fix tạm do thay đổi lại bộ enum, constant
    const e = MSConstant[enumName];

    if (e) {
      for (const key in e) {
        if (e.hasOwnProperty(key) && e[key] === value) {
          return key;
        }
      }
    }

    return value;
  }

  /**
   * Xóa cookie theo key truyền vào
   * @param {*} key 
   */
  deleteCookie(key) {
    document.cookie = key + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  }

  /**
   * Kiểm tra xem có hết hạn license
   * nvxuan 12.10.2023
   */
  checkRouterExpiredDate() {
    const store = useContextStore(),
      overExpiredDate = store?.context?.Context?.OverExpiredDate;

    if (overExpiredDate && !location.href.includes('/logout')) {
      return true;
    }
    return false;
  }

  /**
   * show cảnh báo hết hạn hay không
   * nvxuan 14.09.2023
   */
  checkWarningExpired(proxy, closeWarningDate, context, showWarningExpiredNoti) {
    if (!context) {
      context = useContextStore()?.context?.Context;
    }

    let warningExpired = {},
      showWarningExpiedType = -1,
      wetCfg = {};

    // Loại cảnh báo hết hạn license
    if (context?.WarningExpied) {
      warningExpired = context?.WarningExpied;
      showWarningExpiedType = warningExpired?.ShowWarningExpiedType;
    } else {
      return;
    }

    let expiredDate = warningExpired?.ExpiredDate,
      expiredDateStr = '__/__/____',
      contentNoti = '';

    if (expiredDate) {
      expiredDate = moment(expiredDate).toDate();
      expiredDateStr = `${expiredDate.getDate().toString().padStart(2, '0')}/${(expiredDate.getMonth() + 1)
        .toString()
        .padStart(2, '0')}/${expiredDate.getFullYear()}`;
    }

    switch (showWarningExpiedType) {
      case MSConstant.NotificationType.WarningNearExpiredTrial:
        wetCfg = {
          Top: {
            show: true,
            buttonText: 'Mua ngay',
            content: `Thời gian dùng thử của quý khách còn <b>${
              warningExpired?.DayNearExpired || 0
            } ngày</b>. Vui lòng mua giấy phép để tiếp tục sử dụng phần mềm.`,
            closeWet: () => {
              if (proxy) {
                proxy.wetInfo.Top.show = false;
              }
              if (typeof closeWarningDate == 'function' && closeWarningDate != null) {
                closeWarningDate(1, showWarningExpiedType, context);
              }

              // remove class của body
              document.body.classList.remove('has-expired-notify');
            },
            buyLicense: () => {
              this.buyLicense(1);
            },
          },
          Br: {
            show: true,
            title: 'Sắp hết hạn dùng thử',
            buttonText: 'Mua ngay',
            content: `Thời gian dùng thử của quý khách còn <b>${
              warningExpired?.DayNearExpired || 0
            } ngày</b>. Vui lòng mua giấy phép để tiếp tục sử dụng phần mềm.`,
            buyLicense: () => {
              this.buyLicense(1);
            },
            closeWet: () => {
              if (proxy) {
                proxy.wetInfo.Br.show = false;
              }
              if (typeof closeWarningDate == 'function' && closeWarningDate != null) {
                closeWarningDate(3, showWarningExpiedType, context);
              }
            },
          },
        };

        this.setWarningExpiredDate(proxy, wetCfg, warningExpired);
        break;
      case MSConstant.NotificationType.WarningOverExpiredTrial:
        contentNoti = `Thuê bao sử dụng của quý khách đã quá hạn <b>${
          warningExpired?.NumberOverExpired || 0
        } ngày</b>. Vui lòng mua thuê bao chính thức để sử dụng phần mềm.`;

        wetCfg = {
          Center: {
            title: 'Hết hạn dùng thử phần mềm',
            content: contentNoti,
            buttonText: 'Mua ngay',
            buyLicense: () => {
              this.buyLicense(1);
            },
            showClose: false,
            isOtherTenant: context?.WarningExpied?.CountTenantByUserId >= 2,
          },
        };

        this.setWarningExpiredDate(proxy, wetCfg, warningExpired, true);

        break;
      case MSConstant.NotificationType.WarningNearExpiredOffical:
        wetCfg = {
          Top: {
            show: true,
            buttonText: 'Gia hạn ngay',
            content: `Thời gian sử dụng của quý khách còn <b>${
              warningExpired?.DayNearExpired || 0
            } ngày</b>. Vui lòng gia hạn giấy phép để tiếp tục sử dụng phần mềm.`,
            closeWet: () => {
              if (proxy) {
                proxy.wetInfo.Top.show = false;
              }
              if (typeof closeWarningDate == 'function' && closeWarningDate != null) {
                closeWarningDate(1, showWarningExpiedType, context);
              }

              // remove class của body
              document.body.classList.remove('has-expired-notify');
            },
            buyLicense: () => {
              this.buyLicense(2, showWarningExpiredNoti);
            },
          },
          Br: {
            show: true,
            title: 'Sắp hết hạn sử dụng',
            buttonText: 'Gia hạn ngay',
            content: `Thời gian sử dụng của quý khách còn <b>${
              warningExpired?.DayNearExpired || 0
            } ngày</b>. Vui lòng gia hạn giấy phép để tiếp tục sử dụng phần mềm.`,
            buyLicense: () => {
              this.buyLicense(2, showWarningExpiredNoti);
            },
            closeWet: () => {
              if (proxy) {
                proxy.wetInfo.Br.show = false;
              }

              if (typeof closeWarningDate == 'function' && closeWarningDate != null) {
                closeWarningDate(3, showWarningExpiedType, context);
              }
            },
          },
        };

        this.setWarningExpiredDate(proxy, wetCfg, warningExpired);

        break;
      case MSConstant.NotificationType.WarningExpiredOffical:
        contentNoti = `Thuê bao chính thức của quý khách đã hết hạn từ ngày <b>${expiredDateStr}</b>. Quý khách chỉ được xem chứng từ đã tạo, vui lòng gia hạn giấy phép để tiếp tục sử dụng phần mềm.`;

        wetCfg = {
          Br: {
            show: false,
            title: 'Hết hạn sử dụng phần mềm',
            buttonText: 'Gia hạn ngay',
            content: contentNoti,
            buyLicense: () => {
              this.buyLicense(2, showWarningExpiredNoti);
            },
            collapseForm: true,
          },
          Center: {
            title: 'Hết hạn sử dụng phần mềm',
            content: contentNoti,
            buttonText: 'Gia hạn ngay',
            buyLicense: () => {
              this.buyLicense(2, showWarningExpiredNoti);
            },
            closeWet: () => {
              if (proxy) {
                proxy.wetInfo.Br.show = true;
              }
            },
            showClose: true,
          },
        };

        this.setWarningExpiredDate(proxy, wetCfg, warningExpired, true);

        break;
      default:
        // không có cảnh báo
        break;
    }
  }

  /**
   * Lấy ra resource theo enum
   * @param {Number} value Giá trị của enum cần lấy ra enum resource
   * @param {String} enumName EnumName để get text
   */
  getEnumResource(value, enumName) {
    const me = this;
    const localize = i18n;

    const key = me.getEnumText(value, enumName);
    const resourceKey = enumName + '.' + key;
    const text = localize.t('i18nEnum.' + resourceKey);
    return text;
  }

  /**
   * Lấy enumsource với tên enum truyền vào
   * @param {String} enumName Tên enum trong enumeration
   * @param {Array} values Danh sách các enum lấy ra (nếu cần chỉ định), nếu không thì sẽ lấy tất cả
   * @param {Boolean} prefixIdx Có kèm theo tiền tố index không: true - có, false - không (Mặc định không)
   * VD: getEnumSource("EnumCABAReasonType",[10,11,12,13])
   * CreatedBy: PDKIEN 15/08/2019
   */
  getEnumSource(enumName, values, prefixIdx) {
    const e = MSConstant[enumName];
    const arr = [];
    if (e) {
      let idx = 0;
      for (const key in e) {
        if (e.hasOwnProperty(key)) {
          const v = e[key];
          let ix = -1;
          if (!values || (ix = values.indexOf(v)) > -1) {
            /*
             * sửa lại cách lấy dữ liệu theo enum
             * pvduy 02/02/2020
             */
            let enumText = i18n.t('i18nEnum.' + enumName + '.' + key);
            if (prefixIdx) {
              enumText = ++idx + '. ' + enumText;
            }

            const item = {
              enumValue: v,
              enumKey: key,
              enumText: enumText,
            };

            // lấy thêm tooltip
            if (i18nEnum[enumName + '_Tooltip']) {
              item.tooltip = i18nEnum[enumName + '_Tooltip'][key];
            }
            if (ix > -1) {
              // DVQUAN: sửa lại cách lấy enum source với trường hợp 9 và 99 => không lấy được 1 trong 2
              if (arr[ix]) {
                arr[arr.length] = item;
              } else {
                arr[ix] = item;
              }
            } else {
              arr.push(item);
            }
          }
        }
      }
    }
    return arr;
  }

  /**
   * Lấy tham số trên query string
   */
  getUrlParams(url) {
    let search;

    if (url) {
      const searchStartIdx = url.indexOf('?');
      search = searchStartIdx != -1 ? url.substr(searchStartIdx) : null;
    } else if (window.location.hash) {
      search = window.location.hash.substr(1);
    } else {
      search = window.location.search;
    }

    const temp = new URLSearchParams(search);
    const result = {};
    for (const value of temp.keys()) {
      result[value] = temp.get(value);
    }

    return result;
  }

  /**
   * Đăng xuất khỏi ứng dụng
   * @modified nvthinh 15.11.2023: Cập nhật bất đồng bộ cho logout DVC
   */
  async logoff() {
    // Xóa roleApps khỏi localStorage khi logout
    localStorage.removeItem('allAppsFromLogin');
    localStorage.removeItem('defaultOptionFromLogin');

    const contextStore = useContextStore();
    contextStore.logout();
  }

  downloadLink(link) {
    // const a = document.getElementById('adownload');
    // a.href = link;
    // a.click();

    var iframe = document.createElement('iframe');
    iframe.style.display = 'none';

    iframe.src = link;
    document.body.appendChild(iframe);
  }

  /**
   * Gán cookie
   */
  setCookie(name, value, seconds) {
    let expires = '';
    if (seconds) {
      const date = new Date();
      date.setTime(date.getTime() + seconds * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + (value || '') + expires + '; path=/';
  }

  /**
   * Đọc cookie
   */
  getCookie(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  /**
   * Xóa cookie
   */
  removeCookie(name) {
    document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  }

  /**
   * Xử lý dữ liệu trả về từ server
   * NCPHUOC 19/08/2022: Check xem có đúng là date không thì mới gắn data
   */
  processServerResponseData(data) {
    if (data) {
      const rgDate = /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d/;
      const rgDateTime = /^\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d(.)?(\d{0,9})?(Z)?(\+)?(\d{2})?(:)?(\d{2})?$/;
      for (const i in data) {
        if (data[i]) {
          if (
            typeof data[i] === 'string' &&
            // i.indexOf("Date") > -1 &&
            (rgDate.test(data[i]) || rgDateTime.test(data[i]))
          ) {
            const date = moment(data[i]).toDate();
            if (moment(date).isValid()) {
              data[i] = moment(data[i]).toDate();
            }
          } else if (typeof data[i] === 'object' && Object.keys(data[i].length > 0)) {
            this.processServerResponseData(data[i]);
          }
        }
      }
    }
  }

  /**
   * Lấy text hiển thị của trường thông tin
   * @param {*} field : Tên trường
   * @param {*} namespace : mã để xem có custom lại không, nếu không thì lấy mặc định
   */
  getColumnText(field, namespace) {
    const localize = i18n;
    const common = localize.messages[localize.locale]['i18nColumn'];
    const custom = namespace ? common[namespace] : null;

    if (custom && custom.hasOwnProperty(field)) {
      return custom[field];
    }
    return common[field];
  }

  /**
   * Lấy text hiển thị của bảng
   * @param {*} table : Tên bảng
   * @param {*} namespace : mã để xem có custom lại không, nếu không thì lấy mặc định
   */
  getTableText(table, namespace) {
    const localize = i18n;
    const common = localize.messages[localize.locale]['i18nTable'];
    const custom = namespace ? common[namespace] : null;

    if (custom && custom.hasOwnProperty(table)) {
      return custom[table];
    }
    return common[table];
  }
  /**
   * Maping từ html element thành đối tượng vuejs để thao tác
   * @param {object} dom html dome element
   */
  mapDomToVue(dom) {
    return dom.__vue__;
  }

  /**
   * Chuyển tiếng việt có dấu thành không dấu
   * @author ĐVThi 09/03/2021
   */
  removeVietnameseTones(str) {
    if (str) {
      str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, 'a');
      str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, 'e');
      str = str.replace(/ì|í|ị|ỉ|ĩ/g, 'i');
      str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, 'o');
      str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, 'u');
      str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, 'y');
      str = str.replace(/đ/g, 'd');
      str = str.replace(/À|Á|Ạ|Ả|Ã|Â|Ầ|Ấ|Ậ|Ẩ|Ẫ|Ă|Ằ|Ắ|Ặ|Ẳ|Ẵ/g, 'A');
      str = str.replace(/È|É|Ẹ|Ẻ|Ẽ|Ê|Ề|Ế|Ệ|Ể|Ễ/g, 'E');
      str = str.replace(/Ì|Í|Ị|Ỉ|Ĩ/g, 'I');
      str = str.replace(/Ò|Ó|Ọ|Ỏ|Õ|Ô|Ồ|Ố|Ộ|Ổ|Ỗ|Ơ|Ờ|Ớ|Ợ|Ở|Ỡ/g, 'O');
      str = str.replace(/Ù|Ú|Ụ|Ủ|Ũ|Ư|Ừ|Ứ|Ự|Ử|Ữ/g, 'U');
      str = str.replace(/Ỳ|Ý|Ỵ|Ỷ|Ỹ/g, 'Y');
      str = str.replace(/Đ/g, 'D');
      // Some system encode vietnamese combining accent as individual utf-8 characters
      // Một vài bộ encode coi các dấu mũ, dấu chữ như một kí tự riêng biệt nên thêm hai dòng này
      str = str.replace(/\u0300|\u0301|\u0303|\u0309|\u0323/g, ''); // ̀ ́ ̃ ̉ ̣  huyền, sắc, ngã, hỏi, nặng
      str = str.replace(/\u02C6|\u0306|\u031B/g, ''); // ˆ ̆ ̛  Â, Ê, Ă, Ơ, Ư
      // Remove extra spaces
      // Bỏ các khoảng trắng liền nhau
      str = str.replace(/ + /g, ' ');
      str = str.trim();
      // Remove punctuations
      // Bỏ dấu câu, kí tự đặc biệt
      // str = str.replace(/!|@|%|\^|\*|\(|\)|\+|\=|\<|\>|\?|\/|,|\.|\:|\;|\'|\"|\&|\#|\[|\]|~|\$|_|`|-|{|}|\||\\/g," ");
      return str;
    } else {
      return '';
    }
  }

  buildFilterParam(filters) {
    const me = this;
    let sFilter = '';

    if (filters && !(filters instanceof Array)) {
      filters = [filters];
    }

    const length = filters.length;

    if (length > 0) {
      let i;
      const arrayFilters = [];
      const sJoin = ',';

      for (i = 0; i < length; i++) {
        const filter = filters[i];
        let sChildFilter = '';
        let operandChild = 'and';

        if (filter.childrens instanceof Array && filter.childrens.length > 0) {
          const childFirst = filter.childrens[0];
          if (childFirst.operand) {
            operandChild = childFirst.operand;
          }

          sChildFilter = me.buildFilterParam(filter.childrens);
        }

        let s = '"' + filter.property + '",' + (filter.operator ? '"' + filter.operator + '",' : '');
        // Mặc định operand là `and`
        let operand = '"and"';

        s += me.nomalizeFilterValue(filter.operator, filter.value);
        // xử lý tình huống muôn filter = 0/false nhưng muốn lọc ra cả các giá trị null
        if (filter.nullToFail && (filter.value === 0 || filter.value === false)) {
          s += ',true';
        }

        s = '[' + s + ']';

        if (sChildFilter) {
          s = '[' + s + ',"' + operandChild + '",' + sChildFilter + ']';
        }

        if (i !== 0) {
          if (filter.operand) {
            operand = '"' + filter.operand + '"';
          }
          arrayFilters.add(operand);
        }
        arrayFilters.add(s);
      }

      sFilter = arrayFilters.join(sJoin);
      if (length > 1) {
        sFilter = '[' + sFilter + ']';
      }
    }

    return sFilter;
  }

  nomalizeFilterValue(operator, value) {
    let ret = '';
    switch (operator) {
      case 'in':
      case 'notin':
        ret = JSON.stringify(value);
        break;
      default:
        if (typeof value === 'number') {
          ret = value.toString();
        } else if (typeof value === 'boolean') {
          ret = value;
        } else if (value instanceof Date) {
          if (value.getHours() === 0 && value.getMinutes() === 0 && value.getSeconds() === 0) {
            ret = ['"', moment(value).format('YYYY-MM-DD'), 'T00:00:00"'].join('');
          } else {
            ret = ['"', moment(value).format('YYYY-MM-DDThh:mm:ss'), '"'].join('');
          }
        } else {
          ret = ['"', value, '"'].join('');
        }
    }

    return ret;
  }

  /**
   * Xử lý tham số trước khi gọi lên server load dữ liệu cho combobox
   */
  processComboboxParamRequest(param) {
    const me = this;
    for (const i in param) {
      let value = param[i];
      switch (i) {
        case 'filter':
          // chuẩn hóa tham số filter
          if (Array.isArray(value) && value.length > 0) {
            value = me.buildFilterParam(value);
          } else {
            continue;
          }
          break;
      }
      param[i] = value;
    }
  }

  setLocalStorage(key, value) {
    localStorage.setItem(this.processLocalStorageKey(key), value);
  }

  getLocalStorage(key) {
    return localStorage.getItem(this.processLocalStorageKey(key));
  }

  removeLocalStorage(key) {
    localStorage.removeItem(this.processLocalStorageKey(key));
  }

  processLocalStorageKey(key) {
    return [window._appConfig.localStoragePrefix, key].join('');
  }

  setSessionStorage(key, value) {
    sessionStorage.setItem(key, value);
  }

  getSessionStorage(key) {
    return sessionStorage.getItem(key);
  }
  removeSessionStorage(key) {
    sessionStorage.removeItem(key);
  }

  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  checkFullMonth(fromDate, toDate) {
    let shortText = '';
    const localize = i18n;
    if (fromDate && toDate) {
      if (fromDate.getDate() == 1) {
        const fromYear = fromDate.getFullYear();
        const fromMonth = fromDate.getMonth();
        const toYear = toDate.getFullYear();
        const toMonth = toDate.getMonth();
        const daysOfMonth = 32 - new Date(toYear, toMonth, 32).getDate();
        if (toDate && daysOfMonth == toDate.getDate()) {
          if (fromMonth == toMonth && fromYear == toYear) {
            shortText = localize.t('i18nReport.ReportTerm').format(fromMonth + 1, fromYear);
          } else if (fromMonth < toMonth && fromYear == toYear) {
            shortText = localize.t('i18nReport.ReportTermRange').format(fromMonth + 1, toMonth + 1, fromYear);
          }
          return shortText;
        }
      }
    }
    return shortText;
  }

  helpAms(helpId) {
    return [window._external.amisAmsHelp, helpId].join('');
  }

  /**
   * Func bỏ dấu tiếng việt
   * @param {String} str
   * @author NCThanh1
   */
  removeUnicode(str) {
    let tmp = str;
    tmp = tmp.toLowerCase();
    tmp = tmp.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, 'a');
    tmp = tmp.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, 'e');
    tmp = tmp.replace(/ì|í|ị|ỉ|ĩ/g, 'i');
    tmp = tmp.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, 'o');
    tmp = tmp.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, 'u');
    tmp = tmp.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, 'y');
    tmp = tmp.replace(/đ/g, 'd');
    tmp = tmp.replace(/!|@|%|\^|\*|\(|\)|\+|\=|\<|\>|\?|\/|,|\.|\:|\;|\'|\"|\&|\#|\[|\]|~|\$|_|`|-|{|}|\||\\/g, ' ');
    tmp = tmp.replace(/ + /g, ' ');
    tmp = tmp.trim();
    return tmp;
  }

  /**
   * Load dữ liệu combobox
   */
  getCacheComboboxData(type, payload, loadPromise, loadScope) {
    const key = [type, JSON.stringify(payload)].join('_').toLowerCase();
    const cache = window._cboData[key];

    return new Promise(resolve => {
      if (cache) {
        resolve(cache);
        return;
      }

      loadPromise.call(loadScope, payload).then(result => {
        window._cboData[key] = result;
        resolve(result);
      });
    });
  }

  /**
   * Load dữ liệu combobox
   */
  removeCacheComboboxData(key) {
    const caches = window._cboData;
    if (caches) {
      let keys = [];
      if (Array.isArray(key)) {
        keys = key;
      } else if (typeof key === 'string') {
        keys.push(key);
      } else {
        keys.push(String(key));
      }

      keys.forEach(item => {
        const key = [item, ''].join('_').toLowerCase();
        const removeKeys = [];
        for (const i in caches) {
          if (i.indexOf(key) === 0) {
            removeKeys.push(i);
          }
        }

        for (let i = 0; i < removeKeys.length; i++) {
          const cacheKey = removeKeys[i];
          delete caches[cacheKey];
        }
      });
    }
  }

  /**
   * Tách từ khóa tìm kiếm thành các từ theo quy tắc
   * 1. Nếu có ngoặc kép "" thì tìm đúng kí tự trong ngoặc kép
   * 2. Nếu không thì chỉ cần chứa cả 2 kí tự là đủ
   * @param {string} keyword từ khóa tìm kiếm
   * @author NVLAM 23.11.2020
   */
  splitKeyWord(keyword) {
    let wordArr = [];
    if (typeof keyword !== 'string') {
      keyword = _.toString(keyword);
    }
    const firstDoubleQuoteIdx = keyword.indexOf('"');
    const lastDoubleQuoteIdx = keyword.lastIndexOf('"');
    /**
     * Các TH tìm kiếm full text
     * 1. Đây là foo
     * 2. "Đây là foo
     * 3. Đây "là foo"
     * 4. "Đây là" foo
     */
    if (
      firstDoubleQuoteIdx === -1 ||
      firstDoubleQuoteIdx === lastDoubleQuoteIdx ||
      firstDoubleQuoteIdx !== 0 ||
      lastDoubleQuoteIdx !== keyword.length - 1
    ) {
      wordArr = keyword.split(/\s+/); // tách theo dấu cách
    } else {
      const fullText = keyword.slice(firstDoubleQuoteIdx + 1, lastDoubleQuoteIdx);
      if (!_.isEmpty(fullText)) {
        wordArr.push(fullText);
      }
    }
    // for (let i = 0; i < wordArr.length; ++i) {
    //     wordArr[i] = wordArr[i].replace(/[^a-z\d]/g, '\\$&');//chèn thêm 1 kí từ
    // }
    return wordArr;
  }

  /**
   * hàm search local
   * properties: danh sách các trường cần search
   * searchText: text nhập ở ô search
   * source: dữ liệu gốc dùng để filter
   * grid: grid filter
   */
  filterFn(properties, source, grid, searchText, form) {
    let data = [];
    const me = this;
    const sourceTemp = [];
    if (Array.isArray(source)) {
      Object.assign(sourceTemp, source);
      if (searchText && searchText != '') {
        sourceTemp.forEach(item => {
          if (
            me.checkSearchText(item, properties, searchText) &&
            me.checkFilterHeader(item, grid) &&
            form.checkFilterGridTreeCustom(item, properties, searchText)
          ) {
            if (data.indexOf(item) === -1) {
              data.push(item);
            }
            if (grid && grid.isGridTree) {
              me.findParentNode(source, data, item, form);
            }
          }
        });
      } else {
        data = sourceTemp;
      }
    }
    if (form.filterHeader && form.filterHeader.length > 0) {
      grid.dataTemp = data;
    } else {
      grid.dataTemp = source;
    }
    return data;
  }

  /*
   *  Hàm tìm kiếm các node cha khi thực hiện tìm kiếm dữ liệu.
   *  pvduy 06/01/2021
   */
  findParentNode(items, filterItems, itemChild, form) {
    const me = this;
    let key = '';
    let parentKey = 'ParentID';
    if (form && form.grid && form.grid.idProperty) {
      key = form.grid.idProperty;
    }
    if (form && form.grid && form.grid.parentIdProperty) {
      parentKey = form.grid.parentIdProperty;
    }
    items.forEach(item => {
      if (item[key] == itemChild[parentKey] && !filterItems.find(x => x[key] == item[key])) {
        if (filterItems.indexOf(item) === -1) {
          filterItems.push(item);
        }
        me.findParentNode(items, filterItems, item, form);
      }
    });
  }
  /**
   * hàm filter col
   */
  checkFilterHeader(item, grid) {
    let listFilterHeader = [];
    let check = true;
    listFilterHeader = grid.getFilterHeader(true);
    if (listFilterHeader.length > 0) {
      listFilterHeader.forEach(filter => {
        const value = filter.value ? (filter.value + '').toLowerCase() : '';
        switch (filter.operator) {
          case MSConstant.FilterOperator.Contains:
            if (!(item[filter.property] ? item[filter.property].toLowerCase() : '').contains(value)) {
              check = false;
              return false;
            }
            break;
          case MSConstant.FilterOperator.Notcontains:
            if ((item[filter.property] ? item[filter.property].toLowerCase() : '').contains(value)) {
              check = false;
              return false;
            }
            break;
          case MSConstant.FilterOperator.StartsWith:
            if (!(item[filter.property] ? item[filter.property].toLowerCase() : '').startsWith(value)) {
              return false;
            }
            break;
          case MSConstant.FilterOperator.EndsWith:
            if (!(item[filter.property] ? item[filter.property].toLowerCase() : '').endswith(value)) {
              check = false;
              return false;
            }
            break;
          case MSConstant.FilterOperator.Equals:
            if (item[filter.property] != filter.value) {
              check = false;
              return false;
            }
            break;
          case MSConstant.FilterOperator.NotEquals:
            if (item[filter.property] == filter.value) {
              check = false;
              return false;
            }
            break;
          case MSConstant.FilterOperator.GreaterThan:
            if (item[filter.property] <= filter.value) {
              check = false;
              return false;
            }
            break;
          // case MSConstant.FilterOperator.GreaterThan:
          //   if(item[filter.property] < filter.value){
          //     check = false;
          //     return false;
          //   }
          //   break;
          case MSConstant.FilterOperator.LessThan:
            if (item[filter.property] >= filter.value) {
              check = false;
              return false;
            }
            break;
          case MSConstant.FilterOperator.LessThanEquals:
            if (item[filter.property] > filter.value) {
              check = false;
              return false;
            }
            break;
          case MSConstant.FilterOperator.Between:
            // if(item[filter.property] > filter.value){
            check = false;
            return false;
            // }
            break;
          case MSConstant.FilterOperator.Null:
            if (item[filter.property]) {
              check = false;
              return false;
            }
            break;
          case MSConstant.FilterOperator.NotNull:
            if (!item[filter.property]) {
              check = false;
              return false;
            }
            break;
        }
      });
    }
    return check;
  }
  /**
   * hàm search theo text
   */
  checkSearchText(item, properties, searchText) {
    const me = this;
    let check = false;

    if (searchText) {
      const text = searchText.toLowerCase();
      if (Array.isArray(properties)) {
        properties.forEach(field => {
          if (item[field] && item[field].toString().toLowerCase().contains(text)) {
            check = true;
            return;
          }
        });
      }
    } else {
      return true;
    }
    return check;
  }

  /**
   * Hàm search theo bộ lọc khác (vd theo dõi/ ngừng theo dõi,...)
   */
  checkFilterObject(item, filter) {
    let check = false;

    if (item && filter) {
      const props = Object.keys(filter);

      if (filter && props) {
        props.forEach(field => {
          if (
            item[field] != null &&
            item[field] != undefined &&
            filter[field] != null &&
            filter[field] != undefined &&
            item[field].toString().toLowerCase().contains(filter[field].toString().toLowerCase())
          ) {
            check = true;
            return;
          }
        });
      } else {
        return true;
      }
    }
    return check;
  }

  /**
   * Lấy thông tin phiên đăng nhập
   */
  getContext() {
    const windowKey = '_context';
    let context = window[windowKey];
    if (!context) {
      context = this.getLocalStorage('Context');
      if (context) {
        window[windowKey] = context = JSON.parse(context);
      }
    }

    return context;
  }

  /**
   * xử lý string trước khi render để chống xss
   */
  processXssValue(value) {
    if (typeof value === 'string') {
      // return value.replace(/</g, '&lt');
      return _.escape(value);
    }

    return value;
  }

  /**
   * Xử lý nội dung dữ liệu trước khi binding để tránh tấn công xss
   */
  processXss(item) {
    const me = this;
    const fn = function (obj, lv) {
      lv = lv || 0;
      for (const i in obj) {
        const v = obj[i];
        if (v) {
          if (typeof v === 'string') {
            obj[i] = me.processXssValue(v);
          } else if (typeof v == 'object' && lv < 10 && Object.keys(v).length > 0) {
            fn(v, lv + 1);
          }
        }
      }
    };
    if (Array.isArray(item)) {
      item.forEach(n => {
        fn(n);
      });
    } else if (typeof item === 'object') {
      fn(item);
    }
  }

  /**
   * Xử lý tham số date
   * Xử lý dữ liệu trước khi ném lên server
   * Xử lý để k dính lỗi múi giờ
   */
  processDateParam(param, isDateFn) {
    for (const i in param) {
      const value = param[i];

      if (value && isDateFn(value)) {
        param[i] = moment(value).format('YYYY-MM-DD');
      }
    }
  }

  /**
   * Lấy đường dẫn đầy đủ cho form link
   */
  getFullLink(formLink) {
    const url = [];
    if (location.pathname.toLocaleLowerCase().indexOf('/app/') === 0) {
      url.push('/app');
    }
    url.push(formLink);
    return url.join('');
  }

  /**
   * Lấy tên báo cáo
   */
  getReportName(reportId, defaultName) {
    const localize = i18n;
    const key = 'i18nReport.name.' + reportId;

    const text = localize.t(key);
    return text === key ? defaultName || null : text;
  }

  /**
   * Xóa các phần tử trùng field trong mảng
   * @author: nnnam - 19/11/2020
   * @param {Array} arr Mảng cần distinct
   * @param {...string} args Trường key xác định distinct
   */
  distinct(arr = [], ...args) {
    let result = [];
    if (args.length) {
      result = arr.filter(function (value, index, self) {
        /** Kiểm tra index phần từ đầu tiên thỏa mãn có phải phần tửu hiện tại (không thì loại bỏ) */
        return (
          self.findIndex(function (x) {
            /** Lấy phần từ đầu tiên thỏa mãn */
            return args.every(field => x[field] == value[field]);
          }) === index
        );
      });
    } else {
      /** Mảng chứa mapping sang json để so sánh */
      let arrJson = arr.map(x => JSON.stringify(x));
      /** Loại bỏ các item giống nhau */
      arrJson = [...new Set(arrJson)];
      result = arrJson.map(x => x && JSON.parse(x));
    }
    return result;
  }

  /**
   * Viết hoa chữ cái đầu
   * @author nnnam - 5/3/2021
   * @param {any} str
   */
  capitalize(str) {
    return str ? str[0].toUpperCase() + str.substring(1) : str;
  }

  /**
   * Đọc số thành chữ
   * @author nnnam - 5/3/2021
   * @param {number} num
   */
  sayNumber(num) {
    if (num) {
      let sayNumber = '';
      const maxLength = 15;
      const length = num.toString().length;
      const strNum = ['không', 'một', 'hai', 'ba', 'bốn', 'năm', 'sáu', 'bảy', 'tám', 'chín', 'mười'];

      /**
       * Đọc 1 nhóm số gồm 3 chữ số
       * @author nnnam - 5/3/2021
       * @param {string} grNum
       */
      const sayAGroupDigit = grNum => {
        const sayNums = [];
        if (grNum != '000') {
          const says = ['trăm', 'mươi', ''];
          let nums = grNum.split('').reverse();
          nums.length = 3;
          nums = nums.reverse().map(x => parseInt(x));
          const sayGrNum = nums.map(x => strNum[x]);

          const numHundred = nums[0];
          const numDozen = nums[1];
          const numUnit = nums[2];

          if (numHundred == 0 || numDozen != 0 || numUnit != 0) {
            if (numHundred != undefined) {
              sayNums.push(strNum[numHundred] + ' ' + says[0]);
            }

            if (numDozen != undefined) {
              switch (numDozen) {
                case 0:
                  sayNums.push('linh');
                  break;
                case 1:
                  sayNums.push('mười');
                  break;
                default:
                  sayNums.push(strNum[numDozen] + ' ' + says[1]);
                  break;
              }
            }

            if (numUnit) {
              if (numUnit == 1 && numDozen != 0 && numDozen != 1 && numDozen != undefined) {
                sayNums.push('mốt');
              } else {
                sayNums.push(strNum[numUnit]);
              }
            }
          } else {
            if (numHundred) {
              sayNums.push(strNum[numHundred] + ' ' + says[0]);
            }
          }
        }

        return sayNums.filter(x => x).join(' ');
      };

      /**
       * Chia chuỗi số thành các nhóm 3 chữ số
       * @author nnnam - 5/3/2021
       * @param {string} value
       */
      const splitNumber = function group(value) {
        return value.match(/\d{1,3}(?=(\d{3})*$)/g);
      };

      const countGr = parseInt((length - 1) / 3);
      const grUnit = ['', 'nghìn', 'triệu', 'tỷ', 'nghìn'];
      const splitNum = splitNumber(num.toString()).map((x, i) => sayAGroupDigit(x) + ' ' + grUnit[countGr - i]);
      sayNumber = this.capitalize(splitNum.join(' ').trim());
      return sayNumber;
    } else {
      return 'Không';
    }
  }

  /**
   * Cách đọc số thứ tự
   * @author nnnam - 5/3/2021
   * @param {any} num
   */
  sayOrder(num) {
    if (num == 1) {
      return 'Nhất';
    } else {
      return this.sayNumber(num);
    }
  }

  /**
   * Format số
   * @param {number} num
   * @param {number} count
   */
  formatNumberToString(num, len) {
    let result = num.toString();
    const count = result.length;
    if (num != 0 && count < len) {
      result = '0'.repeat(len - count) + num;
    }
    return result;
  }

  /**
   * Tính toán theo phép tính
   * @author nnnam - 11/6/2021
   * @param {number} a
   * @param {number} b
   * @param {any} operation
   */
  calculateOperation(a, b, operation) {
    switch (operation) {
      case '+':
        return a + b;
      case '-':
        return a - b;
      case '*':
        return a * b;
      case '/':
        return a / b;
    }
  }

  /**
   * Group by theo key
   * @author nnnam - 2/6/2021
   * @param {Array} list
   * @param {any} key
   */
  groupBy(list, key) {
    return list.reduce((obj, item) => {
      obj[item[key]] = [...(obj[item[key]] || []), item];
      return obj;
    }, {});
  }

  /**
   * Tính tổng các số của các đối tượng trong mảng
   * @author nnnam - 2/6/2021
   * @param {Array} list Mảng
   * @param {Array} notNumberFields Các trường group
   */
  summary(list, groupFields, numberFields) {
    list = list || [];
    const first = list[0];

    if (first) {
      /** Mặc định các trường group không phải number */
      groupFields = groupFields || Object.keys(first).filter(key => typeof first[key] != 'number');
      /** Các trường summary không group
       *  @type Array */
      numberFields = numberFields ?? Object.keys(first).filter(key => !groupFields.includes(key));
      /** Lưu json của key */
      const jsonObj = {};
      /** Lấy mảng chứa object key của mảng */
      const mapData = this.distinct(
        list.map((item, i) => {
          const obj = groupFields.reduce((o, key) => {
            o[key] = item[key];
            return o;
          }, {});
          const key = JSON.stringify(obj);
          jsonObj[key] = [...(jsonObj[key] || []), item];
          return obj;
        }),
      );

      /** Tính tổng từng group */
      mapData.forEach(o => {
        const dataGroup = jsonObj[JSON.stringify(o)];
        /** Tính tổng từng thuộc tính trong group */
        numberFields.forEach(field => {
          o[field] = dataGroup.map(x => x[field]).reduce((sum, x) => sum + x, 0);
        });
      });

      return mapData;
    } else {
      return [];
    }
  }

  /**
   * Group mảng theo key
   * @author nnnam - 14/6/2021
   * @param {Array} arr Mangr
   * @param {Function} fnKey Function key
   */
  getGroupByFuncKey(arr, fnKey) {
    return arr.reduce((obj, x) => {
      const key = JSON.stringify(fnKey(x));
      obj[key] = [...(obj[key] || []), x];
      return obj;
    }, {});
  }

  /**
   * Group mảng theo key
   * @author nnnam - 14/6/2021
   * @param {Array} arr Mangr
   * @param {Function} fnKey Function key
   */
  mapGroupByFuncKey(arr, fnKey) {
    return arr.reduce(
      (obj, x) => {
        const key = JSON.stringify(fnKey(x));
        const o = obj.Object[key] || JSON.parse(key);
        obj.Object[key] = o;
        obj.Stack.set(o, [...(obj.Stack.get(o) || []), x]);
        return obj;
      },
      {Stack: new Map(), Object: {}},
    ).Stack;
  }

  /**
   * Fill tất cả các property của các đối tượng trong mảng
   * @author nnnam - 14/6/2021
   * @param {Array} list Mảng
   */
  fillAllProperties(list) {
    /** Clone mảng */
    const result = _.cloneDeep(list);
    /** Tất cả property của object trong mảng */
    const keys = list.reduce((arr, x) => this.distinct(arr.concat(Object.keys(x))), []);
    /** Set các thuộc tính = null */
    list
      .filter(x => Object.keys(x).length != keys.length)
      .forEach(obj => {
        keys
          .filter(key => !obj.hasOwnProperty(key))
          .forEach(key => {
            obj[key] = null;
          });
      });

    return list;
  }

  /**
   * Join chung 2 mảng với nhau
   * @author nnnam - 14/6/2021
   * @param {Array} leftArr
   * @param {Array} rightArr
   * @param {Function} lKey
   * @param {Function} rKey
   * @param {Function} action
   * @param {Function} getKey
   */
  joinArray(leftArr, rightArr, lKey, rKey, action = (l, r) => $.extend({}, r, l), getKey) {
    /** Group 2 mảng */
    const left = this.getGroupByFuncKey(leftArr, lKey);
    const right = this.getGroupByFuncKey(rightArr, rKey);
    /** Lấy value key sau khi group */
    const keys = getKey ? getKey(left, right) : [];
    /** Join 2 bảng với nhau */
    const rs = keys.reduce((arr, key) => {
      const lVal = left[key] || [null];
      const rVal = right[key] || [null];
      /** Lấy kết quả trả về */
      lVal.forEach(l => rVal.forEach(r => arr.push(action(l, r))));
      return arr;
    }, []);

    const result = this.fillAllProperties(rs);
    return result;
  }

  /**
   * Fulljoin 2 mảng với nhau
   * @author nnnam - 14/6/2021
   * @param {Array} leftArr
   * @param {Array} rightArr
   * @param {Function} lKey
   * @param {Function} rKey
   * @param {Function} action
   */
  fullJoin(leftArr, rightArr, lKey, rKey, action = (l, r) => $.extend({}, r, l)) {
    /** Full join các key */
    return this.joinArray(leftArr, rightArr, lKey, rKey, action, (left, right) => {
      return this.distinct(Object.keys(left).concat(Object.keys(right)));
    });
  }

  /**
   * Join 2 mảng với nhau
   * @author nnnam - 14/6/2021
   * @param {Array} leftArr
   * @param {Array} rightArr
   * @param {Function} lKey
   * @param {Function} rKey
   * @param {any} action
   */
  innerJoin(leftArr, rightArr, lKey, rKey, action = (l, r) => $.extend({}, r, l)) {
    /**  Join các key trùng nhau */
    return this.joinArray(leftArr, rightArr, lKey, rKey, action, (left, right) => {
      return Object.keys(left).filter(element => Object.keys(right).includes(element));
    });
  }

  /**
   * LeftJoin 2 mảng với nhau
   * @author nnnam - 14/6/2021
   * @param {Array} leftArr
   * @param {Array} rightArr
   * @param {Function} lKey
   * @param {Function} rKey
   * @param {any} action
   */
  leftJoin(leftArr, rightArr, lKey, rKey, action = (l, r) => $.extend({}, r, l)) {
    /** Lấy key bên trái */
    return this.joinArray(leftArr, rightArr, lKey, rKey, action, (left, right) => {
      return Object.keys(left);
    });
  }

  /**
   * RightJoin 2 mảng với nhau
   * @author nnnam - 14/6/2021
   * @param {Array} leftArr
   * @param {Array} rightArr
   * @param {Function} lKey
   * @param {Function} rKey
   * @param {any} action
   */
  rightJoin(leftArr, rightArr, lKey, rKey, action = (l, r) => $.extend({}, l, r)) {
    /** Lấy key bên phải */
    return this.joinArray(leftArr, rightArr, lKey, rKey, action, (left, right) => {
      return Object.keys(right);
    });
  }

  /**
   * Tính toán lại dữ liệu cây
   * @param {Array} data Dữ liệu
   * @param {string} idField Trường ID
   * @param {string} parentField Trường Id cha
   * @param {Array} numberFields Danh sách trường cần cộng
   * @author nnnam - 25/4/2022
   */
  refreshCalculateTree(data, idField, parentField, numberFields) {
    if (data.length) {
      if (!numberFields || !numberFields.length) {
        numberFields = Object.keys(data[0]).filter(x => x.includes('Amount'));
      }

      const func = node => {
        const childs = data.filter(x => x[parentField] == node[idField]);
        childs.forEach(func);
        childs.length &&
          numberFields.forEach(field => {
            node[field] = childs.reduce((sum, x) => sum + x[field], 0);
          });
      };

      data.filter(x => !x[parentField]).forEach(func);
    }
  }

  /**
   * Tính toán lại dữ liệu cây
   * @param {Array} data Dữ liệu
   * @param {string} idField Trường ID
   * @param {string} parentField Trường Id cha
   * @param {Array} numberFields Danh sách trường cần cộng
   * @author nnnam - 25/4/2022
   */
  refreshCalculateTreeCustom(data, idField, parentField, numberFields, option) {
    if (data.length) {
      option = option || {};
      if (typeof option.filterData == 'function') {
        data = data.filter(option.filterData);
      }
      if (!numberFields || !numberFields.length) {
        numberFields = Object.keys(data[0]).filter(x => x.includes('amount'));
      }

      const func = node => {
        const childs = data.filter(x => x[parentField] == node[idField]);
        childs.forEach(func);
        childs.length &&
          numberFields.forEach(field => {
            node[field] = childs.reduce((sum, x) => sum + (x[field] || 0), 0);
          });
      };

      data.filter(x => !x[parentField]).forEach(func);
    }
  }

  /**
   * Build config table
   * @param {Array} rows
   * @author nnnam - 28/4/2022
   */
  getConfigHeader(rows) {
    /** Số dòng */
    const len = rows.length;
    /** Đệ quy */
    const rs = rows[0].reduce(
      (obj, col) => {
        /**
         * Hàm đệ quy build config
         * @param {any} col Config cột
         * @param {any} iRow Thứ tự dòng
         * @param {any} rSpan Total rowspan
         * @param {any} skip Có bỏ qua các dòng sau không?
         */
        const func = (col, iRow, rSpan, skip) => {
          const colspan = col.colspan || 1;
          const rowspan = col.rowspan || 1;
          /** Độ dài Rowspan */
          const lenRowspan = rSpan + rowspan;

          /** Tăng thứ tự stack */
          obj.Childs[iRow] = (obj.Childs[iRow] || 0) + 1;

          /** Kiểm tra điều kiện build */
          skip = !skip && colspan == 1 && (col.field || rowspan == len - iRow);
          /** Thêm vào stack */
          skip && obj.Cols.push(col);

          /** Nếu chưa phải dòng cuối */
          if (lenRowspan < len) {
            /** Tăng thứ tự dòng */
            iRow++;
            /** Lấy trong stack thứ tự ô đã xét */
            let i = obj.Childs[iRow] || 0;
            /** Tối đã ô có thể xét */
            const maxColRow = rows[iRow].length;
            /** Đếm colspan */
            let count = 0;

            /** Tìm các config con để đệ quy */
            while (i < maxColRow && count < colspan && iRow <= len) {
              const col = rows[iRow][i++];
              count += col.colspan || 1;
              func(col, iRow, lenRowspan, skip);
            }
          }
        };

        func(col, 0, 0);

        return obj;
      },
      {
        /** Stack lưu index các ô đã xét */
        Childs: [],
        /** Lưu config build */
        Cols: [],
      },
    );

    return rs.Cols;
  }

  /**
   * Tính toán giá trị theo công thức truyền vào
   * @param {any} obj Các giá trị của các node con ({C2: 2, C3: 3})
   * @param {any} formula Công thức tính (C2 + C3)
   * @author: nnnam - 25/4/2022
   */
  calculateByFormula(obj = {}, formula) {
    let result = 0;

    if (formula) {
      const that = this;
      /** Bỏ các khoảng trắng */
      formula = formula.replaceAll(' ', '');

      const sum = ['+', '-'];
      const multi = ['*', '/'];
      /** Mảng tree đệ quy */
      const stack = [
        {
          Key: formula,
          Opera: '+',
          Parent: undefined,
          Grade: 0,
          Id: 0,
          Value: 0,
        },
      ];

      const func = (item, opera) => {
        /** Loại bỏ các dấu () đầu và cuối */
        const mat = item.Key.match(/^\((.*)\)$/);
        mat && (item.Key = mat[1]);

        /** Hash formula theo công thức */
        const tpReg = `[${opera.map(x => '\\' + x).join('|')}]\(?![^\\(]*\\)\)`;
        const reg = new RegExp(tpReg);
        const regG = new RegExp(tpReg, 'g');

        /** Nếu Hash đc */
        if (item.Key.match(reg)) {
          /** Hash element */
          const ele = item.Key.split(reg);
          /** Hash formula */
          const operas = [...item.Key.match(regG)].map(x => x[0]);
          /** Mặc định dấu đầu là dấu (+) */
          operas.length < ele.length && operas.unshift('+');

          /** Hash element and save to Stack */
          item.Key.split(reg).forEach((str, i) => {
            const oItem = {
              Key: str,
              Opera: i ? operas[i] : '+',
              Parent: item.Id,
              Grade: item.Grade + 1,
              Id: stack.length,
            };
            stack.push(oItem);
            func(oItem, opera.includes('+') ? multi : sum);
          });
        } else if (item.Key.match(/\+|\-|\*|\//)) {
          func(item, opera.includes('+') ? multi : sum);
        }
      };

      /** Hash phần tử đầu */
      func(stack[0], sum);

      /** Lấy các node lá và gán giá trị */
      const leafs = stack.filter(x => stack.every(o => o.Parent != x.Id));
      leafs.forEach(x => (x.Value = obj[x.Key] || 0));

      // /** Tính giá trị cho các node cha */
      const calculate = item => {
        const childs = stack.filter(c => c.Parent == item.Id);
        if (childs.length) {
          childs.forEach(calculate);

          item.Value = childs.reduce((val, x) => that.calculateOperation(val, x.Value, x.Opera), 0);
        }
      };

      calculate(stack[0]);

      /** Lấy giá trị */
      result = stack[0].Value;
    }

    return result;
  }

  /**
   * Tính toán lại dữ liệu cây
   * @param {Array} data Dữ liệu
   * @param {string} idField Trường ID
   * @param {string} parentField Trường Id cha
   * @param {Array} numberFields Danh sách trường cần cộng
   * @author nnnam - 25/4/2022
   */
  calculateTree(data, key, field, idField, parentField, formulaCol) {
    if (data.length) {
      const that = this;
      /** Mặc định công thức cột rỗng */
      formulaCol = formulaCol || {};

      /** Băm công thức cột */
      const formulaHashCol = Object.keys(formulaCol).reduce(
        (obj, key) => {
          /** @type string */
          const fomuCol = formulaCol[key].replaceAll(' ', '');
          /** Tách cột */
          const spl = fomuCol.split(/\+|\-|\*|\\/);
          /** Lưu lại các cột */
          obj.Hash[key] = spl;
          /** Lưu lại tất cả cột */
          obj.All = obj.All.concat(spl);
          return obj;
        },
        {Hash: {}, All: [field]},
      );

      /** Băm cột */
      const hashCol = formulaHashCol.Hash;
      /** Tất cả các cột */
      const allCols = this.distinct(formulaHashCol.All);
      /** Các cột root */
      const colRoots = Object.keys(hashCol).filter(col => allCols.every(x => col != x));

      /** Tìm các node cha */
      const findParents = function (obj, key, hash) {
        const parents = Object.keys(hash).filter(x => hash[x].some(o => o == key));
        obj.Item = that.distinct(obj.Item.concat(parents));
        parents.forEach(key => {
          obj.Child = obj.Child.concat(hash[key]);
          findParents(obj, key, hash);
        });

        obj.Child = that.distinct(obj.Child.filter(child => obj.Item.every(o => o != child)));
        return obj;
      };

      /** Các cột root */
      const rootColRs = findParents({Item: [], Child: [field]}, field, hashCol);
      /** Filter cột root */
      const rootCols = rootColRs.Item.filter(x => colRoots.some(o => o == x));
      let rows = [];

      /** Tính toán dòng */
      const calculateRow = node => {
        const parents = data.filter(x => x[idField] == node[parentField]);

        rows = [...rows, ...parents];

        parents.forEach(parent => {
          // Nếu trạng thái dòng cha là none thì cần gán lại state để cập nhật db
          if (parent.state == 0) {
            parent.state = 2;
          }
          const childs = data.filter(x => x[parentField] == parent[idField]);

          rootColRs.Child.forEach(field => {
            parent[field] = childs.reduce((sum, child) => sum + parseFloat(child[field] || 0), 0);
          });

          calculateRow(parent);
        });
      };

      /** Tính toán cột */
      const calculateCol = function (col) {
        const childs = hashCol[col];
        const items = childs.filter(x => rootColRs.Item.some(o => o == x));
        items.forEach(calculateCol);

        /** For từng dòng */
        rows.forEach(row => {
          const express = childs.reduce((o, x) => ({...o, [x]: row[x]}), {});
          row[col] = that.calculateByFormula(express, formulaCol[col]);
        });
      };

      /** Tính toán dòng */
      data.filter(x => x[idField] == key).forEach(node => calculateRow(node));
      /** Tính toán cột */
      rootCols.forEach(calculateCol);
    }
  }

  /**
   * Tính toán dữ liệu bằng công thức
   * @param {Array} data dữ liệu bảng
   * @param {any} keyField : DataField tính toán nằm trong formula
   * @param {any} key : VD: row[keyField]
   * @param {any} field : DataField đang sửa
   * @param {any} formulaCol : Công thức cột: {A: 'B + C'} (Cộng ngang)
   * @param {any} formulaField
   * @param {any} isGrid : Là grid
   * @author nnam - 23/5/2022
   */
  calculateByFormulaColumn(data, keyField, key, field, formulaCol, formulaField = 'formula', isGrid) {
    if (data) {
      /** Mặc định công thức cột rỗng */
      formulaCol = formulaCol || {};

      const that = this;

      /** Băm công thức cột */
      const formulaHashCol = Object.keys(formulaCol).reduce(
        (obj, key) => {
          /** @type string */
          const fomuCol = formulaCol[key].replaceAll(' ', '');
          /** Tách cột */
          const spl = fomuCol.split(/\+|\-|\*|\\/);
          /** Lưu lại các cột */
          obj.Hash[key] = spl;
          /** Lưu lại tất cả cột */
          obj.All = obj.All.concat(spl);
          return obj;
        },
        {Hash: {}, All: [field]},
      );

      /** Băm công thức dòng */
      const formulaHashRow = data
        .filter(x => x[formulaField])
        .reduce(
          (obj, item) => {
            const key = item[keyField];
            /** @type string */
            const formu = item[formulaField].replaceAll(' ', '');
            /** Tách công thức */
            const spl = formu.split(/\+|\-|\*|\\/);
            /** Lưu lại công thức */
            obj.Row[key] = formu;
            /** lưu cột */
            obj.Hash[key] = spl;
            /** Lưu tất cả cột */
            obj.All = obj.All.concat(spl);
            return obj;
          },
          {Row: {}, Hash: {}, All: [key]},
        );

      /** Băm cột */
      const hashCol = formulaHashCol.Hash;
      /** Băm dòng */
      const fomulaRow = formulaHashRow.Row;
      /** Các cột sau khi băm */
      const hashRow = formulaHashRow.Hash;
      /** Tất cả các key */
      const allKeys = that.distinct(formulaHashRow.All);
      /** Tất cả các cột */
      const allCols = that.distinct(formulaHashCol.All);
      /** Các key cha */
      const parentRoots = Object.keys(fomulaRow).filter(key => allKeys.every(x => x != key));
      /** Các cột root */
      const colRoots = Object.keys(hashCol).filter(col => allCols.every(x => col != x));

      /** Tìm các node cha */
      const findParents = function (obj, key, hash) {
        const parents = Object.keys(hash).filter(x => hash[x].some(o => o == key));
        obj.Item = that.distinct(obj.Item.concat(parents));
        parents.forEach(key => {
          obj.Child = obj.Child.concat(hash[key]);
          findParents(obj, key, hash);
        });

        obj.Child = that.distinct(obj.Child.filter(child => obj.Item.every(o => o != child)));
        return obj;
      };

      /** Các cột root */
      const rootColRs = findParents({Item: [], Child: [field]}, field, hashCol);
      /** Các dòng root */
      const rootRowRs = findParents({Item: [], Child: [key]}, key, hashRow);
      /** Filter cột root */
      const rootCols = rootColRs.Item.filter(x => colRoots.some(o => o == x));
      /** Filter dòng root */
      const rootRows = rootRowRs.Item.filter(x => parentRoots.some(o => o == x));
      /** Các dòng chứa công thức */
      const rows = rootRowRs.Child.concat(rootRowRs.Item).reduce(
        (obj, x) => ({...obj, [x]: data.filter(o => o[keyField] == x)}),
        {},
      );
      /** Lấy các dòng công thức tiêu biểu */
      const rowItems = Object.keys(rows).reduce((obj, key) => ({...obj, [key]: rows[key] && rows[key][0]}), {});

      /** Tính toán dòng */
      const calculateRow = function (key) {
        const childs = hashRow[key];
        const items = childs.filter(x => rootRowRs.Item.some(o => o == x));
        items.forEach(calculateRow);

        /** For từng cột */
        rootColRs.Child.forEach(col => {
          const express = childs.reduce((o, x) => {
            o[x] = rowItems[x][col];
            return o;
          }, {});
          const val = that.calculateByFormula(express, fomulaRow[key]);
          rows[key].forEach(item => {
            item[col] = val;
            if (isGrid) {
              // Xử lý state nếu empty => Insert, nếu None thì gán về Update
              if (item.state === ModelState.Empty) {
                item.state = ModelState.Insert;
              } else if (item.state === ModelState.None) {
                item.state = ModelState.Update;
              }
            }
          });
        });
      };

      /** Tính toán cột */
      const calculateCol = function (col) {
        const childs = hashCol[col];
        const items = childs.filter(x => rootColRs.Item.some(o => o == x));
        items.forEach(calculateCol);

        /** For từng dòng */
        Object.keys(rowItems).forEach(key => {
          const express = childs.reduce((o, x) => ({...o, [x]: rowItems[key][x]}), {});
          const val = that.calculateByFormula(express, formulaCol[col]);
          rows[key].forEach(item => (item[col] = val));
        });
      };

      /** Tính toán dòng */
      rootRows.forEach(calculateRow);
      /** Tính toán cột */
      rootCols.forEach(calculateCol);
    }
  }

  /**
   * Tính toán lại dữ liệu bằng công thức
   * @param {Array} data
   * @param {any} keyField
   * @param {any} formulaCol
   * @param {Array} cols Các cột cần tình mà không có trong công thức
   * @param {any} formulaField
   * @author nnam - 23/5/2022
   */
  refreshByFormulaColumn(data, keyField, formulaCol, cols, formulaField = 'Formula') {
    if (data) {
      const that = this;
      /** Mặc định công thức cột rỗng */
      formulaCol = formulaCol || {};
      cols = cols || [];

      /** Băm công thức cột */
      const formulaHashCol = Object.keys(formulaCol).reduce(
        (obj, key) => {
          /** @type string */
          const fomuCol = formulaCol[key].replaceAll(' ', '');
          /** Tách cột */
          const spl = fomuCol.split(/\+|\-|\*|\\/);
          /** Lưu lại các cột */
          obj.Hash[key] = spl;
          /** Lưu lại tất cả cột */
          obj.All = obj.All.concat(spl);
          return obj;
        },
        {Hash: {}, All: []},
      );

      /** Băm công thức dòng */
      const formulaHashRow = data
        .filter(x => x[formulaField])
        .reduce(
          (obj, item) => {
            const key = item[keyField];
            /** @type string */
            const formu = item[formulaField].replaceAll(' ', '');
            /** Tách công thức */
            const spl = formu.split(/\+|\-|\*|\\/);
            /** Lưu lại công thức */
            obj.Row[key] = formu;
            /** lưu cột */
            obj.Hash[key] = spl;
            /** Lưu tất cả cột */
            obj.All = obj.All.concat(spl);
            return obj;
          },
          {Row: {}, Hash: {}, All: []},
        );

      /** Băm cột */
      const hashCol = formulaHashCol.Hash;
      /** Băm dòng */
      const fomulaRow = formulaHashRow.Row;
      /** Các cột sau khi băm */
      const hashRow = formulaHashRow.Hash;
      /** Tất cả các key */
      const allKeys = this.distinct(formulaHashRow.All);
      /** Tất cả các cột */
      const allCols = this.distinct(formulaHashCol.All);
      /** Các key cha */
      const rootRows = Object.keys(fomulaRow).filter(key => allKeys.every(x => x != key));
      /** Các cột root */
      const rootCols = Object.keys(hashCol).filter(col => allCols.every(x => col != x));
      const childCols = allCols.concat(cols).filter(x => !formulaCol.hasOwnProperty(x));

      /** Các dòng chứa công thức */
      const rows = this.distinct(allKeys.concat(rootRows)).reduce(
        (obj, x) => ({...obj, [x]: data.filter(o => o[keyField] == x)}),
        {},
      );
      /** Lấy các dòng công thức tiêu biểu */
      const rowItems = Object.keys(rows).reduce((obj, key) => ({...obj, [key]: rows[key] && rows[key][0]}), {});

      /** Tính toán dòng */
      const calculateRow = function (key) {
        const childs = hashRow[key];

        if (childs) {
          childs.forEach(calculateRow);

          /** For từng cột */
          childCols.forEach(col => {
            const express = childs.reduce((o, x) => ({...o, [x]: rowItems[x][col]}), {});
            const val = that.calculateByFormula(express, fomulaRow[key]);
            rows[key].forEach(item => (item[col] = val));
          });
        }
      };

      /** Tính toán cột */
      const calculateCol = function (col) {
        const childs = hashCol[col];
        if (childs) {
          childs.forEach(calculateCol);

          /** For từng dòng */
          Object.keys(rowItems).forEach(key => {
            const express = childs.reduce((o, x) => ({...o, [x]: rowItems[key][x]}), {});
            const val = that.calculateByFormula(express, formulaCol[col]);
            rows[key].forEach(item => (item[col] = val));
          });
        }
      };

      /** Tính toán dòng */
      rootRows.forEach(calculateRow);
      /** Tính toán cột */
      rootCols.forEach(calculateCol);
    }
  }

  /**
   * Hàm chuẩn hóa tham số
   */
  nomalizeLoadParameter(payload) {
    if (!payload.skip) {
      delete payload.skip;
    }

    if (payload.filter && payload.filter.length > 0) {
      payload.filter = JSON.stringify(payload.filter);
    } else {
      delete payload.filter;
    }

    if (payload.condition && Object.keys(payload.condition).length > 0) {
      payload.condition = JSON.stringify(payload.condition);
    } else {
      delete payload.condition;
    }

    if (payload.columns && payload.columns.length > 0) {
      payload.columns = payload.columns.join(',');
    } else {
      delete payload.columns;
    }
  }

  /**
   * Set từ ngày đến ngày theo kì báo cáo
   * @author nnnam - 20/7/2022
   */
  getDateRange(period, data, flag, type) {
    /**
     * Lấy dữ liệu ngày hiện tại theo ngày HT
     * NXCHIEN 26.07.2022
     */
    const getPostedDate = flag => {
      if (flag && flag.isCheck) {
        return moment(flag.value).toDate();
      }
      return new Date();
    };

    const dateRange = {};
    if (typeof period !== 'undefined') {
      // Lấy thông tin tuần
      const dateWeek = getPostedDate(flag);

      const day = dateWeek.getDay() === 0 ? 7 : dateWeek.getDay(); // NXCHIEN tính lại ngày đầu tuần
      const first = dateWeek.getDate() - day + 1;
      const last = first + 6;
      const firstWeek = new Date(getPostedDate(flag).setDate(first)).toUTCString();
      const lastWeek = new Date(getPostedDate(flag).setDate(last)).toUTCString();

      // Lấy thông tin tháng
      const dateMonth = getPostedDate(flag);
      const firstMonth = new Date(dateMonth.getFullYear(), dateMonth.getMonth(), 1);

      const lastMonth = new Date(dateMonth.getFullYear(), dateMonth.getMonth() + 1, 0);

      const startCurrentQuater = moment(getPostedDate(flag))
        .quarter(moment(getPostedDate(flag)).quarter())
        .startOf('quarter');

      const endCurrentQuater = moment(getPostedDate(flag))
        .quarter(moment(getPostedDate(flag)).quarter())
        .endOf('quarter');

      if (!type || type == 'Period' || type == 'ReportPeriodCustom' || type == 'PeriodReport') {
        switch (period) {
          case MSConstant.Period.ThisDay:
            dateRange.fromDate = moment(dateWeek).format('YYYY-MM-DD');
            dateRange.toDate = moment(dateWeek).format('YYYY-MM-DD');
            break;

          case MSConstant.Period.ThisWeek:
            dateRange.fromDate = moment(firstWeek).format('YYYY-MM-DD');
            dateRange.toDate = moment(lastWeek).format('YYYY-MM-DD');
            break;

          case MSConstant.Period.FirstWeekToPresent:
            dateRange.fromDate = moment(firstWeek).format('YYYY-MM-DD');
            dateRange.toDate = moment(dateWeek).format('YYYY-MM-DD');
            break;
          case MSConstant.PeriodReport.CustomReport:
            // Bổ sung loại tùy chọn cho enum đang để cùng tháng dẫn đến bị sai
            dateRange.fromDate = data?.fromDate;
            dateRange.toDate = data?.toDate;
            break;
          case MSConstant.Period.ThisMonth:
            dateRange.fromDate = moment(firstMonth).format('YYYY-MM-DD');
            dateRange.toDate = moment(lastMonth).format('YYYY-MM-DD');
            break;

          case MSConstant.Period.FirstMonthToPresent:
            dateRange.fromDate = moment(firstMonth).format('YYYY-MM-DD');
            dateRange.toDate = moment(dateWeek).format('YYYY-MM-DD');
            break;

          case MSConstant.Period.ThisQuarter:
            dateRange.fromDate = startCurrentQuater.format('YYYY-MM-DD');
            dateRange.toDate = endCurrentQuater.format('YYYY-MM-DD');
            break;

          case MSConstant.Period.FirstQuarterToPresent:
            dateRange.fromDate = startCurrentQuater.format('YYYY-MM-DD');
            dateRange.toDate = moment(dateWeek).format('YYYY-MM-DD');
            break;

          case MSConstant.Period.ThisYear:
            dateRange.fromDate = moment(new Date(dateWeek.getFullYear(), 0, 1)).format('YYYY-MM-DD');
            dateRange.toDate = moment(new Date(dateWeek.getFullYear(), 11, 31)).format('YYYY-MM-DD');
            break;

          case MSConstant.Period.FirstYearToPresent:
            dateRange.fromDate = moment(new Date(dateWeek.getFullYear(), 0, 1)).format('YYYY-MM-DD');
            dateRange.toDate = moment(dateWeek).format('YYYY-MM-DD');
            break;

          case MSConstant.Period.SixFirstMonth:
            dateRange.fromDate = moment(new Date(dateWeek.getFullYear(), 0, 1)).format('YYYY-MM-DD');
            dateRange.toDate = moment(new Date(dateWeek.getFullYear(), 6, 0)).format('YYYY-MM-DD');
            break;

          case MSConstant.Period.SixLastMonth:
            dateRange.fromDate = moment(new Date(dateWeek.getFullYear(), 6, 1)).format('YYYY-MM-DD');
            dateRange.toDate = moment(new Date(dateWeek.getFullYear(), 11, 31)).format('YYYY-MM-DD');
            break;

          case MSConstant.Period.January:
            dateRange.fromDate = moment(new Date(dateWeek.getFullYear(), 0, 1)).format('YYYY-MM-DD');
            dateRange.toDate = moment(new Date(dateWeek.getFullYear(), 1, 0)).format('YYYY-MM-DD');
            break;

          case MSConstant.Period.February:
            dateRange.fromDate = moment(new Date(dateWeek.getFullYear(), 1, 1)).format('YYYY-MM-DD');
            dateRange.toDate = moment(new Date(dateWeek.getFullYear(), 2, 0)).format('YYYY-MM-DD');
            break;

          case MSConstant.Period.March:
            dateRange.fromDate = moment(new Date(dateWeek.getFullYear(), 2, 1)).format('YYYY-MM-DD');
            dateRange.toDate = moment(new Date(dateWeek.getFullYear(), 3, 0)).format('YYYY-MM-DD');
            break;

          case MSConstant.Period.April:
            dateRange.fromDate = moment(new Date(dateWeek.getFullYear(), 3, 1)).format('YYYY-MM-DD');
            dateRange.toDate = moment(new Date(dateWeek.getFullYear(), 4, 0)).format('YYYY-MM-DD');
            break;

          case MSConstant.Period.May:
            dateRange.fromDate = moment(new Date(dateWeek.getFullYear(), 4, 1)).format('YYYY-MM-DD');
            dateRange.toDate = moment(new Date(dateWeek.getFullYear(), 5, 0)).format('YYYY-MM-DD');
            break;

          case MSConstant.Period.June:
            dateRange.fromDate = moment(new Date(dateWeek.getFullYear(), 5, 1)).format('YYYY-MM-DD');
            dateRange.toDate = moment(new Date(dateWeek.getFullYear(), 6, 0)).format('YYYY-MM-DD');
            break;

          case MSConstant.Period.July:
            dateRange.fromDate = moment(new Date(dateWeek.getFullYear(), 6, 1)).format('YYYY-MM-DD');
            dateRange.toDate = moment(new Date(dateWeek.getFullYear(), 7, 0)).format('YYYY-MM-DD');
            break;

          case MSConstant.Period.August:
            dateRange.fromDate = moment(new Date(dateWeek.getFullYear(), 7, 1)).format('YYYY-MM-DD');
            dateRange.toDate = moment(new Date(dateWeek.getFullYear(), 8, 0)).format('YYYY-MM-DD');
            break;

          case MSConstant.Period.September:
            dateRange.fromDate = moment(new Date(dateWeek.getFullYear(), 8, 1)).format('YYYY-MM-DD');
            dateRange.toDate = moment(new Date(dateWeek.getFullYear(), 9, 0)).format('YYYY-MM-DD');
            break;

          case MSConstant.Period.October:
            dateRange.fromDate = moment(new Date(dateWeek.getFullYear(), 9, 1)).format('YYYY-MM-DD');
            dateRange.toDate = moment(new Date(dateWeek.getFullYear(), 10, 0)).format('YYYY-MM-DD');
            break;

          case MSConstant.Period.November:
            dateRange.fromDate = moment(new Date(dateWeek.getFullYear(), 10, 1)).format('YYYY-MM-DD');
            dateRange.toDate = moment(new Date(dateWeek.getFullYear(), 11, 0)).format('YYYY-MM-DD');
            break;

          case MSConstant.Period.December:
            dateRange.fromDate = moment(new Date(dateWeek.getFullYear(), 11, 1)).format('YYYY-MM-DD');
            dateRange.toDate = moment(new Date(dateWeek.getFullYear(), 11, 31)).format('YYYY-MM-DD');
            break;

          case MSConstant.Period.FiscalI:
            dateRange.fromDate = moment(dateWeek).quarter(1).startOf('quarter').format('YYYY-MM-DD');
            dateRange.toDate = moment(dateWeek).quarter(1).endOf('quarter').format('YYYY-MM-DD');
            break;

          case MSConstant.Period.FiscalII:
            dateRange.fromDate = moment(dateWeek).quarter(2).startOf('quarter').format('YYYY-MM-DD');
            dateRange.toDate = moment(dateWeek).quarter(2).endOf('quarter').format('YYYY-MM-DD');
            break;

          case MSConstant.Period.FiscalIII:
            dateRange.fromDate = moment(dateWeek).quarter(3).startOf('quarter').format('YYYY-MM-DD');
            dateRange.toDate = moment(dateWeek).quarter(3).endOf('quarter').format('YYYY-MM-DD');
            break;

          case MSConstant.Period.FiscalIV:
            dateRange.fromDate = moment(dateWeek).quarter(4).startOf('quarter').format('YYYY-MM-DD');
            dateRange.toDate = moment(dateWeek).quarter(4).endOf('quarter').format('YYYY-MM-DD');
            break;

          case MSConstant.Period.Yesterday:
            dateRange.fromDate = moment(dateWeek).subtract(1, 'days').startOf('day').format('YYYY-MM-DD');
            dateRange.toDate = moment(dateWeek).subtract(1, 'days').endOf('day').format('YYYY-MM-DD');
            break;

          case MSConstant.Period.LastWeek:
            dateRange.fromDate = moment(dateWeek).subtract(1, 'weeks').startOf('week').format('YYYY-MM-DD');
            dateRange.toDate = moment(dateWeek).subtract(1, 'weeks').endOf('week').format('YYYY-MM-DD');
            break;

          case MSConstant.Period.LastMonth:
            dateRange.fromDate = moment(dateWeek).subtract(1, 'months').startOf('month').format('YYYY-MM-DD');
            dateRange.toDate = moment(dateWeek).subtract(1, 'months').endOf('month').format('YYYY-MM-DD');
            break;

          case MSConstant.Period.LastQuarter:
            dateRange.fromDate = moment(dateWeek)
              .quarter(moment(dateWeek).quarter())
              .subtract(1, 'Q')
              .startOf('quarter')
              .format('YYYY-MM-DD');
            dateRange.toDate = moment(dateWeek)
              .quarter(moment(dateWeek).quarter())
              .subtract(1, 'Q')
              .endOf('quarter')
              .format('YYYY-MM-DD');
            break;

          case MSConstant.Period.LastYear:
            dateRange.fromDate = moment(dateWeek).subtract(1, 'years').startOf('year').format('YYYY-MM-DD');
            dateRange.toDate = moment(dateWeek).subtract(1, 'years').endOf('year').format('YYYY-MM-DD');
            break;

          case MSConstant.Period.Tomorrow:
            dateRange.fromDate = moment(dateWeek).subtract(-1, 'days').startOf('day').format('YYYY-MM-DD');
            dateRange.toDate = moment(dateWeek).subtract(-1, 'days').endOf('day').format('YYYY-MM-DD');
            break;
          case MSConstant.Period.NextWeek:
            dateRange.fromDate = moment(dateWeek).subtract(-1, 'weeks').startOf('week').format('YYYY-MM-DD');
            dateRange.toDate = moment(dateWeek).subtract(-1, 'weeks').endOf('week').format('YYYY-MM-DD');
            break;

          case MSConstant.Period.NextMonth:
            dateRange.fromDate = moment(dateWeek).subtract(-1, 'months').startOf('month').format('YYYY-MM-DD');
            dateRange.toDate = moment(dateWeek).subtract(-1, 'months').endOf('month').format('YYYY-MM-DD');
            break;

          case MSConstant.Period.NextQuarter:
            dateRange.fromDate = moment(dateWeek)
              .quarter(moment(dateWeek).quarter())
              .subtract(-1, 'Q')
              .startOf('quarter')
              .format('YYYY-MM-DD');
            dateRange.toDate = moment(dateWeek)
              .quarter(moment(dateWeek).quarter())
              .subtract(-1, 'Q')
              .endOf('quarter')
              .format('YYYY-MM-DD');
            break;

          case MSConstant.Period.NextYear:
            dateRange.fromDate = moment(dateWeek).subtract(-1, 'years').startOf('year').format('YYYY-MM-DD');
            dateRange.toDate = moment(dateWeek).subtract(-1, 'years').endOf('year').format('YYYY-MM-DD');
            break;
          case MSConstant.PeriodReport.Next4Week:
          case MSConstant.ReportPeriodCustom.Next4Week:
            dateRange.fromDate = moment(dateWeek).subtract(-1, 'weeks').startOf('week').format('YYYY-MM-DD');
            dateRange.toDate = moment(dateWeek).subtract(-4, 'weeks').endOf('week').format('YYYY-MM-DD');
            break;
          case MSConstant.Period.Custom:
            dateRange.fromDate = data?.fromDate;
            dateRange.toDate = data?.toDate;
            break;

          default:
            dateRange.fromDate = null;
            dateRange.toDate = null;
            break;
        }
      } else {
        const year = getPostedDate(flag).getFullYear();
        const month = getPostedDate(flag).getMonth();
        const curMonthQ = month - (month % 3);

        const arr = [
          {
            from: month,
            to: month + 1,
            val: MSConstant.ReportPeriod.ThisMonth,
          },
          /** Tháng */
          {from: 0, to: 1, val: MSConstant.ReportPeriod.January},
          {from: 1, to: 2, val: MSConstant.ReportPeriod.February},
          {from: 2, to: 3, val: MSConstant.ReportPeriod.March},
          {from: 3, to: 4, val: MSConstant.ReportPeriod.April},
          {from: 4, to: 5, val: MSConstant.ReportPeriod.May},
          {from: 5, to: 6, val: MSConstant.ReportPeriod.June},
          {from: 6, to: 7, val: MSConstant.ReportPeriod.July},
          {from: 7, to: 8, val: MSConstant.ReportPeriod.August},
          {from: 8, to: 9, val: MSConstant.ReportPeriod.September},
          {from: 9, to: 10, val: MSConstant.ReportPeriod.October},
          {from: 10, to: 11, val: MSConstant.ReportPeriod.November},
          {from: 11, to: 12, val: MSConstant.ReportPeriod.December},

          {
            from: curMonthQ,
            to: curMonthQ + 3,
            val: MSConstant.ReportPeriod.ThisQuarter,
          },

          /** Quý */
          {from: 0, to: 3, val: MSConstant.ReportPeriod.QuarterI},
          {from: 3, to: 6, val: MSConstant.ReportPeriod.QuarterII},
          {from: 6, to: 9, val: MSConstant.ReportPeriod.QuarterIII},
          {from: 9, to: 12, val: MSConstant.ReportPeriod.QuarterIV},
          /** 6 tháng */
          {from: 0, to: 6, val: MSConstant.ReportPeriod.FirtsHalfYear},
          {from: 6, to: 12, val: MSConstant.ReportPeriod.SecondHalfYear},
          /** Năm */
          {from: 0, to: 12, val: MSConstant.ReportPeriod.Year},
        ];

        const item = arr.find(x => x.val == period);

        const getDay = day => moment(day).format('YYYY-MM-DD');

        if (item) {
          dateRange.fromDate = getDay(new Date(year, item.from, 1));
          dateRange.toDate = getDay(new Date(year, item.to, 0));
        } else {
          dateRange.fromDate = data?.fromDate;
          dateRange.toDate = data?.toDate;
        }
      }
    }
    dateRange.periodText = this.getEnumResource(
      period || MSConstant.Period.Custom || MSConstant.PeriodReport.Custom,
      type || 'Period',
    );

    return dateRange;
  }

  /**
   * Lấy kỳ theo Ngày bắt đầu, Ngày kết thúc
   * @author nnnam - 22/7/2022
   */
  getPeriodByDate(fromDate, toDate, flag, type, current) {
    let period = MSConstant.Period.Custom;
    const isReport = type == 'ReportPeriod';
    /**
     * Lấy dữ liệu ngày hiện tại theo ngày HT
     * NXCHIEN 26.07.2022
     */
    const getPostedDate = flag => {
      if (flag && flag.isCheck) {
        return moment(moment(flag.value).format('YYYY-MM-DD')).toDate();
      }
      return new Date();
    };

    if (fromDate && toDate) {
      fromDate = moment(fromDate).toDate().getDateOnly();
      toDate = moment(toDate).toDate().getDateOnly();

      const curDte = getPostedDate(flag);

      const fromDay = fromDate.getDate();
      const fromMonth = fromDate.getMonth() + 1;
      const fromYear = fromDate.getFullYear();

      const nextToDate = moment(toDate).toDate().addDays(1);
      const nextToDay = nextToDate.getDate();
      const nextToMonth = nextToDate.getMonth() + 1;
      const nextToYear = nextToDate.getFullYear();

      const year = curDte.getFullYear();
      const month = curDte.getMonth() + 1;
      const curMonthQ = month - ((month - 1) % 3) - 1;

      const startCurrentQuater = moment(curDte).quarter(moment(curDte).quarter()).startOf('quarter');

      const endCurrentQuater = moment(curDte).quarter(moment(curDte).quarter()).endOf('quarter');

      let item;

      /** Giá trị map các tháng và giá trị */
      const months = [
        /** Tháng này */
        {
          from: month,
          to: month + 1,
          val: MSConstant.Period.ThisMonth,
          report: MSConstant.ReportPeriod.ThisMonth,
        },
        {
          from: month - 1 || 12,
          to: month,
          val: MSConstant.Period.LastMonth,
          year: month - 1 ? year : year - 1,
        },
        {
          from: month + 1,
          to: month + 2,
          val: MSConstant.Period.NextMonth,
          year: month + 1 < 13 ? year : year + 1,
        },
        {
          from: month,
          to: month + 1,
          val: MSConstant.Period.FirstMonthToPresent,
        },
        /** Tháng */
        {
          from: 1,
          to: 2,
          val: MSConstant.Period.January,
          report: MSConstant.ReportPeriod.January,
        },
        {
          from: 2,
          to: 3,
          val: MSConstant.Period.February,
          report: MSConstant.ReportPeriod.February,
        },
        {
          from: 3,
          to: 4,
          val: MSConstant.Period.March,
          report: MSConstant.ReportPeriod.March,
        },
        {
          from: 4,
          to: 5,
          val: MSConstant.Period.April,
          report: MSConstant.ReportPeriod.April,
        },
        {
          from: 5,
          to: 6,
          val: MSConstant.Period.May,
          report: MSConstant.ReportPeriod.May,
        },
        {
          from: 6,
          to: 7,
          val: MSConstant.Period.June,
          report: MSConstant.ReportPeriod.June,
        },
        {
          from: 7,
          to: 8,
          val: MSConstant.Period.July,
          report: MSConstant.ReportPeriod.July,
        },
        {
          from: 8,
          to: 9,
          val: MSConstant.Period.August,
          report: MSConstant.ReportPeriod.August,
        },
        {
          from: 9,
          to: 10,
          val: MSConstant.Period.September,
          report: MSConstant.ReportPeriod.September,
        },
        {
          from: 10,
          to: 11,
          val: MSConstant.Period.October,
          report: MSConstant.ReportPeriod.October,
        },
        {
          from: 11,
          to: 12,
          val: MSConstant.Period.November,
          report: MSConstant.ReportPeriod.November,
        },
        {
          from: 12,
          to: 13,
          val: MSConstant.Period.December,
          report: MSConstant.ReportPeriod.December,
        },
        {
          from: curMonthQ + 1,
          to: month + 1,
          val: MSConstant.Period.FirstQuarterToPresent,
        },
        /** Quý này */
        {
          from: curMonthQ + 1,
          to: curMonthQ + 4,
          val: MSConstant.Period.ThisQuarter,
          report: MSConstant.ReportPeriod.ThisQuarter,
        },
        {
          from: curMonthQ - 2,
          to: curMonthQ + 1,
          val: MSConstant.Period.LastQuarter,
        },
        {
          from: curMonthQ + 4,
          to: curMonthQ + 7,
          val: MSConstant.Period.NextQuarter,
        },
        /** Quý */
        {
          from: 1,
          to: 4,
          val: MSConstant.Period.FiscalI,
          report: MSConstant.ReportPeriod.QuarterI,
        },
        {
          from: 4,
          to: 7,
          val: MSConstant.Period.FiscalII,
          report: MSConstant.ReportPeriod.QuarterII,
        },
        {
          from: 7,
          to: 10,
          val: MSConstant.Period.FiscalIII,
          report: MSConstant.ReportPeriod.QuarterIII,
        },
        {
          from: 10,
          to: 13,
          val: MSConstant.Period.FiscalIV,
          report: MSConstant.ReportPeriod.QuarterIV,
        },
        /** 6 tháng */
        {
          from: 1,
          to: 7,
          val: MSConstant.Period.SixFirstMonth,
          report: MSConstant.ReportPeriod.FirtsHalfYear,
        },
        {
          from: 7,
          to: 13,
          val: MSConstant.Period.SixLastMonth,
          report: MSConstant.ReportPeriod.SecondHalfYear,
        },
        /** Năm */
        {
          from: 1,
          to: 13,
          val: MSConstant.Period.ThisYear,
          report: MSConstant.ReportPeriod.Year,
        },
        {from: 1, to: 13, val: MSConstant.Period.LastYear, year: year - 1},
        {from: 1, to: 13, val: MSConstant.Period.NextYear, year: year + 1},
      ];

      /** Nếu tròn tháng */
      if (
        fromDay == 1 &&
        nextToDay == 1 &&
        (current != MSConstant.Period.FirstYearToPresent ||
          this.compareDate(curDte, toDate) ||
          fromMonth != 1 ||
          fromYear != year)
      ) {
        var items = months.filter(
          x =>
            x.from == fromMonth && x.to == nextToMonth + (nextToYear - fromYear) * 12 && fromYear == (x.year || year),
        );
        if (isReport) {
          items = items.filter(x => x.report);
          item = items.find(x => x.report == current) || items[0];
          period = item?.report;
        } else {
          item = items.find(x => x.val == current) || items[0];
          period = item?.val;
        }
      } else if (!isReport) {
        // Lấy thông tin tuần
        const now = getPostedDate(flag).getDateOnly();
        const day = now.getDay() === 0 ? 7 : now.getDay(); // NXCHIEN tính lại ngày đầu tuần
        const first = now.getDate() - day + 1;

        const firstWeek = new Date(new Date(now).setDate(first));
        const lastWeek = new Date(new Date(now).setDate(first + 6));

        /** Case theo ngày */
        const arr = [
          {from: now, to: now, val: MSConstant.Period.ThisDay},
          {
            from: new Date(now).addDays(-1),
            to: new Date(now).addDays(-1),
            val: MSConstant.Period.Yesterday,
          },
          {
            from: new Date(now).addDays(1),
            to: new Date(now).addDays(1),
            val: MSConstant.Period.Tomorrow,
          },
          {
            from: new Date(firstWeek).addDays(-7),
            to: new Date(lastWeek).addDays(-7),
            val: MSConstant.Period.LastWeek,
          },
          {
            from: new Date(firstWeek).addDays(7),
            to: new Date(lastWeek).addDays(7),
            val: MSConstant.Period.NextWeek,
          },
          {from: firstWeek, to: lastWeek, val: MSConstant.Period.ThisWeek},
          {
            from: firstWeek,
            to: now,
            val: MSConstant.Period.FirstWeekToPresent,
          },
          {
            from: new Date(year, month - 1, 1),
            to: now,
            val: MSConstant.Period.FirstMonthToPresent,
          },
          {
            from: new Date(year, 0, 1),
            to: now,
            val: MSConstant.Period.FirstYearToPresent,
          },
          {
            from: startCurrentQuater.format('YYYY-MM-DD'),
            to: moment(curDte).format('YYYY-MM-DD'),
            val: MSConstant.Period.FirstQuarterToPresent,
          },
          {
            from: new Date(firstWeek).addDays(7),
            to: new Date(lastWeek).addDays(28),
            val: MSConstant.ReportPeriodCustom.Next4Week,
          },
        ];
        var items = arr.filter(x => moment(x.from).isSame(fromDate) && moment(x.to).isSame(toDate));

        item = items.find(x => x.val == current) || items[0];
        item && (period = item.val);
      }
    }

    period = period || (isReport ? MSConstant.ReportPeriod.Custom : period);

    // Nếu giá trị period không nằm trong enum type thì lấy mặc định Tùy chọn
    const enumSources = this.getEnumSource(type, [period]);
    if (!(Array.isArray(enumSources) && enumSources.length > 0)) {
      period = MSConstant.ReportPeriod.Custom;
    }

    const periodText = this.getEnumResource(period, type || 'Period');
    return {period, periodText};
  }

  /**
   * Kiểm tra dữ liệu có phải dạng JSON hay ko
   * HQMINH 25.07.2022
   */
  isJson(str) {
    const bValue = true;
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return bValue;
  }

  /** Sắp xếp data theo tree
   * @author nnnam - 2/11/2020
   * @param {Array} data Dữ liệu sắp xếp
   * @param {string} fieldID Trường sắp xếp, key
   * @param {string} parentID ID của parent
   */
  sortTreeData(data, fieldID = 'id', parentID = 'ParentID') {
    /** Mảng kết quả sau khi sắp xếp theo tree */
    const sort = [];
    data = data.distinctkey(fieldID);
    /** Những node root grade 1 */
    const roots = data.filter(x => !x[parentID]);

    /**
     * Quy nạp Tìm node con => Thêm vào mảng kết quả
     * @author nnnam - 2/11/2020
     * @param {any} node Node cha
     */
    const initGroup = function (node) {
      /** Danh sách node con */
      const childs = data.filter(x => node[fieldID] && x[parentID] == node[fieldID]);
      /** Có phải là cha hay không? */
      node._isParent = !!childs.length;
      sort.push(node);
      childs.forEach(child => {
        child._grade = node._grade + 1;
        initGroup(child);
      });
    };

    roots.forEach(item => {
      item._grade = 1;
      initGroup(item);
    });

    return sort;
  }

  /**
   * Tìm những node cha trên tree
   * @author: nnnam - 3/11/2020
   * @param {any} data Dữ liệu tree
   * @param {any} node Node hiện tại
   * @param {any} fieldID Trường ID key để xác định node
   * @param {any} parentID ID node cha
   * @param {any} hasCurrent Có lấy đơn vị hiện tại không?
   */
  findParentNode(data, node, fieldID = 'id', parentID = 'ParentID', hasCurrent) {
    /** Mảng kết quả trả về chứa danh sách node cha */
    const parents = [];

    /**
     * Đệ quy tìm node cha của node hiện tại
     * @param {any} item Node hiện tại
     * @param {boolean} isCurrent Có Thêm node hiện tại ko?
     */
    const findParent = function (item, isCurrent) {
      /** Node cha */
      const parent = data.find(
        x =>
          x[fieldID] &&
          (x[fieldID] && x[fieldID].toLocaleLowerCase()) == (item[parentID] && item[parentID].toLocaleLowerCase()),
      );
      /** Nếu có node cha thì đệ quy tìm các node cao hơn */
      parent && findParent(parent);
      /** Nếu không có node hiện tại thì không Thêm */
      !isCurrent && parents.push(item);
    };

    findParent(node, !hasCurrent);
    return parents;
  }

  /**
   * Tìm những node cha trên tree
   * @author: nnnam - 3/11/2020
   * @param {any} data Dữ liệu tree
   * @param {any} id Node hiện tại
   * @param {any} fieldID Trường ID key để xác định node
   * @param {any} parentID ID node cha
   * @param {any} hasCurrent Có lấy đơn vị hiện tại không?
   */
  findParentsById(data, id, fieldID = 'id', parentID = 'ParentID', hasCurrent) {
    const node = data.find(x => x[fieldID] == id);
    return this.findParentNode(data, node, fieldID, parentID, hasCurrent);
  }

  /**
   * Tìm những node con trên tree
   * @author nnnam - 3/11/2020
   * @param {any} data Dữ liệu tree
   * @param {any} node Node hiện tại
   * @param {any} fieldID Trường ID key để xác định node
   * @param {any} parentID ID node cha
   * @param {any} hasCurrent Có lấy đơn vị hiện tại không?
   */
  findChildNode(data, node, fieldID, parentID, hasCurrent) {
    /** Mảng kết quả trả về chứa danh sách node con */
    const childs = [];
    fieldID = fieldID || 'id';
    parentID = parentID || 'ParentID';

    /**
     * Đệ quy tìm node con của node hiện tại
     * @param {any} item Node hiện tại
     * @param {boolean} isCurrent Có Thêm node hiện tại ko?
     */
    const findChilds = function (item, isCurrent) {
      /** Node con */
      const childArr = data.filter(
        x =>
          x[fieldID] &&
          (x[parentID] && x[parentID].toLocaleLowerCase()) == (item[fieldID] && item[fieldID].toLocaleLowerCase()),
      );
      /** Nếu không có node hiện tại thì không Thêm */
      !isCurrent && childs.push(item);
      /** Nếu có node con thì đệ quy tìm các node thấp hơn */
      childArr.forEach(child => findChilds(child));
    };

    findChilds(node, !hasCurrent);
    return childs;
  }

  /**
   * Tìm những node con trên tree
   * @author nnnam - 3/11/2020
   * @param {any} data Dữ liệu tree
   * @param {any} node Node hiện tại
   * @param {any} fieldID Trường ID key để xác định node
   * @param {any} parentID ID node cha
   * @param {any} hasCurrent Có lấy đơn vị hiện tại không?
   */
  findChildsById(data, id, fieldID, parentID, hasCurrent) {
    const node = data.find(x => x[fieldID] == id);
    return this.findChildNode(data, node, fieldID, parentID, hasCurrent);
  }

  /**
   * Tìm những node con trên tree
   * @author nnnam - 3/11/2020
   * @param {any} data Dữ liệu tree
   * @param {any} node Node hiện tại
   * @param {any} fieldID Trường ID key để xác định node
   * @param {any} parentID ID node cha
   * @param {any} hasCurrent Có lấy đơn vị hiện tại không?
   */
  findChildsByField(data, id, fieldID, childField, hasCurrent) {
    const node = data.find(x => x[fieldID] == id);
    const childs = [];
    fieldID = fieldID || 'id';
    childField = childField || 'childNodes';

    /**
     * Đệ quy tìm node con của node hiện tại
     * @param {any} item Node hiện tại
     * @param {boolean} isCurrent Có Thêm node hiện tại ko?
     */
    const findChilds = function (item, isCurrent) {
      /** Node con */
      const childArr = item[childField] || [];
      /** Nếu không có node hiện tại thì không Thêm */
      !isCurrent && childs.push(item);
      /** Nếu có node con thì đệ quy tìm các node thấp hơn */
      childArr.forEach(child => findChilds(child));
    };

    node && findChilds(node, !hasCurrent);
    return childs;
  }

  /**
   * Tìm những node con chi tiết nhất (node lá) trên tree
   * @author nnnam - 13/11/2020
   * @param {Array} data Dữ liệu tree
   * @param {object} node Node hiện tại
   * @param {string} fieldID Trường ID key để xác định node
   * @param {string} parentID ID node cha
   * @param {boolean} hasCurrent Có lấy đơn vị hiện tại không?
   */
  findLeafNodes(data, node, fieldID, parentID, hasCurrent) {
    /** Mảng kết quả trả về chứa danh sách node con */
    const childs = MISAUtil.findChildNode(data, node, fieldID, parentID, hasCurrent);
    const result = childs.filter(o => {
      /** Lấy những node con */
      if (o.hasOwnProperty('_isParent')) {
        return !o._isParent;
      } else {
        return !childs.some(x => x[parentID] == o[fieldID]);
      }
    });

    return result;
  }

  /**
   * Tìm những node con trên tree
   * @author nnnam - 3/11/2020
   * @param {any} data Dữ liệu tree
   * @param {any} node Node hiện tại
   * @param {any} fieldID Trường ID key để xác định node
   * @param {any} parentID ID node cha
   * @param {any} hasCurrent Có lấy đơn vị hiện tại không?
   */
  checkIsParent(data, par, id, fieldID, parentID, isCurrent) {
    let isParent = !!isCurrent && par == id;
    const node = data.find(x => x[fieldID] == id);

    /**
     * Đệ quy tìm node cha của node hiện tại
     * @param {any} item Node hiện tại
     */
    const findParent = function (item) {
      /** Node cha */
      const parent = data.find(
        x =>
          x[fieldID] &&
          (x[fieldID] && x[fieldID].toLocaleLowerCase()) == (item[parentID] && item[parentID].toLocaleLowerCase()),
      );

      if (parent) {
        if (par == parent[fieldID]) {
          isParent = true;
        } else {
          /** Nếu có node cha thì đệ quy tìm các node cao hơn */
          findParent(parent);
        }
      }
    };

    findParent(node);
    return isParent;
  }

  /**
   * Hàm lấy ra moment để không phải import moment
   * NVTUAN1 17.08.2022
   * TODO Replace tất cả các chỗ đang import và sử dụng moment thành getMoment
   */
  getMoment(date) {
    // Nếu không truyền tham số ngày tháng thì return đối tượng moment ngày hiện tại
    if (!date) {
      return moment();
    }

    return moment(date);
  }

  /**
   * Build công thức sql load lên các tài khoản con chi tiết nhất thuộc tk cha đã được chọn
   * @param {array} arr Mảng truyền vào
   */
  buildOrOpera(arr, field, opera) {
    const rs = arr.reduce(
      (obj, item) => {
        const build = {
          Field: field,
          Operator: opera,
          Value: item,
        };
        obj.Stack && (obj.Stack.Ors = [build]);
        obj.Value || (obj.Value = build);
        return {Stack: build, Value: obj.Value};
      },
      {Value: undefined, Stack: undefined},
    );

    return rs.Value;
  }

  /**
   * Compare ngày
   * @author nnnam - 9/9/2022
   */
  compareDate(dte, compareDte) {
    const dte1 = moment(dte).format('YYYY-MM-DD');
    const dte2 = moment(compareDte).format('YYYY-MM-DD');

    return dte1 == dte2 ? MSConstant.Compare.Equal : dte1 > dte2 ? MSConstant.Compare.Than : MSConstant.Compare.Less;
  }

  /**
   * Xử lý làm tròn số theo dboption
   * @param {*} optionsKey dboption key
   * @param {*} value
   * @return
   * NCPHUOC 09/09/2022
   */
  roundDecimalDbOption(optionsKey, value) {
    const dbOptions = useDbOptionStore().state;
    const numberVal = Number(value);
    if (optionsKey && Number.isInteger(dbOptions[optionsKey]) && !isNaN(numberVal)) {
      return Number(numberVal.toFixed(dbOptions[optionsKey]));
    }
    return value;
  }

  /**
   * Gen tự động guid uuid v4
   */
  newGuid() {
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
      (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16),
    );
  }

  /**
   * Tạo object date từ string
   * @param {*} dateString
   * @param {*} format
   * nxhoa 14.10.2022
   */
  dateFromString(dateString, format) {
    let dateObject;
    try {
      const dateMomentObject = moment(dateString, format); // 1st argument - string, 2nd argument - format
      dateObject = dateMomentObject.toDate(); // convert moment.js object to Date object
    } catch (e) {}
    return dateObject;
  }

  /**
   * Thay đổi trạng thái của 1 dòng theo trạng thái trước đó
   * NCPHUOC 27/10/2022
   */
  changeStateRow(row) {
    if (row) {
      switch (row.state) {
        case MSConstant.ModelState.Insert:
        case MSConstant.ModelState.Empty:
          row.state = MSConstant.ModelState.Insert;
          break;
        case MSConstant.ModelState.Update:
        case MSConstant.ModelState.None:
          row.state = MSConstant.ModelState.Update;
          break;
        case MSConstant.ModelState.Delete:
          break;
        default:
          row.state = MSConstant.ModelState.Insert;
      }
    }
  }

  /**
   * Xử lý filter mảng
   * @author nnnam - 11/11/2022
   */
  filterArray(arr, filter) {
    const me = this;
    arr = arr || [];
    filter = filter || [];
    filter = Array.isArray(filter) ? filter : JSON.parse(filter);

    const rs = arr.filter(item => me.checkFilter(item, filter));
    return rs;
  }

  /**
   * Kiểm tra phần tử thỏa mãn filter
   * @author nnnam - 11/11/2022
   */
  checkFilter(item, filter) {
    const me = this;
    return filter.every(
      flt =>
        me.checkFilterByOpera(item[flt.Field], flt.Value, flt.Operator) || (flt.Ors && me.checkFilter(item, flt.Ors)),
    );
  }
  /**
   * Kiểm tra phần tử thỏa mãn điều kiền theo công thức
   * @author nnnam - 11/11/2022
   */
  checkFilterByOpera(value, compare, opera) {
    let rs = false;
    const str = value || '';
    switch (opera) {
      case MSConstant.FilterOperator.Contains:
        rs = str.includes(compare);
        break;
      case MSConstant.FilterOperator.Notcontains:
        rs = !str.includes(compare);
        break;
      case MSConstant.FilterOperator.StartsWith:
        rs = str.startsWith(compare);
        break;
      case MSConstant.FilterOperator.EndsWith:
        rs = str.endsWith(compare);
        break;
      case MSConstant.FilterOperator.Null:
        rs = value === null || value === undefined;
        break;
      case MSConstant.FilterOperator.NotNull:
        rs = value === null || value === undefined;
        break;
      case MSConstant.FilterOperator.Equals:
        rs = value === compare || (value === 1 && compare === true) || (value === 0 && compare === false);
        break;
      case MSConstant.FilterOperator.NotEquals:
        rs = value !== compare && !(value === 1 && compare === true) && !(value === 0 && compare === false);
        break;
      case MSConstant.FilterOperator.GreaterThan:
        rs = value > compare;
        break;
      case MSConstant.FilterOperator.GreaterThanEquals:
        rs = value >= compare;
        break;
      case MSConstant.FilterOperator.LessThan:
        rs = value < compare;
        break;
      case MSConstant.FilterOperator.LessThanEquals:
        rs = value <= compare;
        break;
      case MSConstant.FilterOperator.Between:
        compare = compare || [];
        rs = value >= compare[0] && value <= compare[1];
        break;
      case MSConstant.FilterOperator.In:
        compare = compare || [];
        rs = compare.includes(value);
        break;
      case MSConstant.FilterOperator.NotIn:
        compare = compare || [];
        rs = !compare.includes(value);
        break;
      default:
        rs = value === compare || (value === 1 && compare === true) || (value === 0 && compare === false);
        break;
    }
    return rs;
  }

  /**
   * Build sort method
   * @param {any} a
   * @param {any} b
   * NXCHIEN 18.02.2022
   */
  sortMethodAsc(a, b) {
    return a == b ? 0 : a > b ? 1 : -1;
  }

  /**
   * Build sort từ người dùng truyền vào
   * @param {any} direction asc hoặc desc
   * NXCHIEN 18.02.2022
   */
  sortMethodWithDirection(direction) {
    const me = this;
    if (direction === undefined || direction == 'asc') {
      return me.sortMethodAsc;
    } else {
      return function (a, b) {
        return -me.sortMethodAsc(a, b);
      };
    }
  }

  /**
   * Build đối tượng sort theo 1 điều kiện
   * @param {any} columnName
   * @param {any} direction
   * NXCHIEN 18.02.2022
   */
  sortMethodWithDirectionByColumn(columnName, direction) {
    const me = this;
    const sortMethod = me.sortMethodWithDirection(direction);
    return function (a, b) {
      return sortMethod(a[columnName], b[columnName]);
    };
  }

  /**
   * Build đối tượng sort với nhiều điều kiện
   * @param {any} sortArray [{ column: "column5", direction: "asc" },{ column: "column3", direction: "desc" }]
   * Ex: arr.sort(MISAUtil.sortMethodWithDirectionMultiColumn([{ column: "SortOrder", direction: "asc" }, { column: "MISACode", direction: "asc" }]));
   * EX: sortData: [{ sortField: "SortOrder", sortType: 1}]
   * NXCHIEN 18.02.2022
   */
  sortMethodWithDirectionMultiColumn(sortData) {
    const me = this;

    const sortArray = sortData.map(x => {
      return {
        column: x.sortField,
        direction: x.sortType == 1 ? 'desc' : 'asc',
      };
    });

    const sortMethodsForColumn = (sortArray || []).map(item =>
      me.sortMethodWithDirectionByColumn(item.column, item.direction),
    );
    return function (a, b) {
      let sorted = 0;
      let index = 0;
      while (sorted === 0 && index < sortMethodsForColumn.length) {
        sorted = sortMethodsForColumn[index++](a, b);
      }
      return sorted;
    };
  }

  /**
   * sort array theo tham số
   * EX sortArray(arr, [{ sortField: "SortOrder", sortType: 1}])
   */
  sortArray(arr, sort) {
    return arr.sort((a, b) => {
      return sort
        .map(x => {
          const valA = a[x.sortField];
          const valB = b[x.sortField];
          const opera = x.sortType == 1 ? -1 : 1;
          return (
            valA !== valB &&
            opera * ([valA, valB].some(t => typeof t === 'string') ? valA?.localeCompare(valB) : valA > valB ? 1 : -1)
          );
        })
        .find(x => x);
    });
  }

  /**
   * Check quyền
   * TTLONG 07/12/2022
   * Hiện tại chưa dùng
   */
  checkPermission(permission) {
    const store = useContextStore(),
      IsAdmin = store.IsAdmin,
      {Permission} = store.context,
      route = useRoute(),
      router = useRouter();

    // Nếu là admin thì cho pass luôn
    if (IsAdmin) {
      permission.isPermission = true;
    } else {
      // Thực hiện check permision
      if (Permission) {
        const paths = route.path.split('/');
        if (paths.length > 0) {
          const subSystemRouterKey = paths[paths.length - 1];
          const subSystem = MSConstant.MappingRouterSubSystem[subSystemRouterKey];
          if (
            (Permission[subSystem] && Permission[subSystem].some(x => (x & MSConstant.Permission.View) === 0)) ||
            !Permission[subSystem]
          ) {
            permission.isPermission = true;
          } else {
            router.push({name: '403'});
          }
        }
      } else {
        router.push({name: '403'});
      }
    }
  }

  // chỉ check các nghiệp vụ này thôi, do trong thiết lập vai trò thì chỉ có những nghiệp vụ này
  subSystemCodeCheck = [
    'etreasury',
    'budgetplan',
    'cash',
    'deposits',
    'receipt',
    'tools',
    'favoucherqlts',
    'salary',
    'summary',
  ];

  /**
   * check quyền trước khi thực hiện show popup
   * nvxuan 12.10.2023
   */
  checkPermissionForShowPopup(popupName) {
    const contextStore = useContextStore(),
      // Là vai trò Admin hay không
      isAdmin = contextStore?.IsAdmin,
      // thông tin thiết lập quyền hạn vai trò
      permissionCfg = contextStore?.context?.Permission,
      // ds các nghiệp vụ không được phép sử dụng
      businessPers = Array.isArray(permissionCfg?.UnActiveBusinessPermission)
        ? permissionCfg?.UnActiveBusinessPermission.map(e => e.permission_code.toLowerCase())
        : [],
      cfg = popupUtil._registerPopup;

    let subSystemCodeMap;

    // Là admin thì toàn quyền
    if (isAdmin) {
      return true;
    }

    popupName = popupName.toLowerCase();

    if (cfg != null) {
      Object.keys(cfg).filter(code => {
        if (cfg[code].includes(popupName)) {
          subSystemCodeMap = code;
          return;
        }
      });

      if (
        subSystemCodeMap &&
        this.subSystemCodeCheck.includes(subSystemCodeMap) &&
        businessPers.includes(subSystemCodeMap)
      ) {
        return false;
      }
    }

    return true;
  }

  /**
   * check quyền trước khi in báo cáo
   * nvxuan 24.12.2023
   */
  checkPermissionForReport(reportId, isShowAlert) {
    const contextStore = useContextStore(),
      // Là vai trò Admin hay không
      isAdmin = contextStore?.IsAdmin,
      // thông tin thiết lập quyền hạn vai trò
      permissionCfg = contextStore?.context?.Permission,
      // ds các báo cáo được phép sử dụng
      reportPers = Array.isArray(permissionCfg?.UnActiveReportPermission)
        ? permissionCfg?.UnActiveReportPermission.map(e => e.permission_code)
        : [],
      // ds các báo cáo luôn được xem, ko cần check
      ignoreCheckReport = [];

    if (isAdmin) return true;

    // không có quyền xem báo cáo thì show cảnh báo
    if (reportId && !ignoreCheckReport.includes(reportId) && reportPers.includes(reportId)) {
      if (isShowAlert) {
        showAlert(
          'Vui lòng liên hệ Quản trị dữ liệu để được phân quyền sử dụng báo cáo này',
          'Người dùng không được cấp quyền',
        );
      }
      return false;
    }

    return true;
  }

  /**
   * check quyền dựa vào subsystemcode
   * tvhien 12.12.2022
   */
  checkPermissionWithSubSystemCode(permission, subSystemCode) {
    const me = this,
      store = useContextStore(),
      IsAdmin = store.IsAdmin,
      {Permission} = store.context;

    // Không có hoặc khi là admin thì đều null
    if (!Permission || IsAdmin) {
      return true;
    }

    if (!subSystemCode) {
      return true;
    }

    // permission có trong danh sách quyền hay không - tvhien - 07.12.2022
    const allowPermissions = Permission[subSystemCode];
    if (allowPermissions && allowPermissions.some(x => (x & permission) != 0)) {
      return false;
    }
    return true;
  }

  /**
   * Check vào subsysytem theo quyền hạn của vai trò
   * nvxuan 11.10.2023
   */
  checkCodeByRolePermission(item) {
    const contextStore = useContextStore(),
      // Là vai trò Admin hay không
      isAdmin = contextStore?.IsAdmin,
      // thông tin thiết lập quyền hạn vai trò
      permissionCfg = contextStore?.context?.Permission,
      // ds các nghiệp vụ không được phép sử dụng
      businessPers = Array.isArray(permissionCfg?.UnActiveBusinessPermission)
        ? permissionCfg?.UnActiveBusinessPermission.map(e => e.permission_code.toLowerCase())
        : [],
      // chỉ check các nghiệp vụ này thôi, do trong thiết lập vai trò thì chỉ có những nghiệp vụ này
      innerCode = this.subSystemCodeCheck,
      itemCode = item.code && typeof item.code == 'string' ? item.code.toLowerCase() : item.code;

    // Là admin thì full quyền
    if (isAdmin) {
      return true;
    }

    if (itemCode && innerCode.includes(itemCode) && businessPers.includes(itemCode)) {
      showAlert(
        'Vui lòng liên hệ Quản trị dữ liệu để được phân quyền sử dụng nghiệp vụ này',
        'Người dùng không được cấp quyền',
      );
      return false;
    }

    return true;
  }

  /**
   * check phân quyền dựa theo route truyền bào
   */
  checkPermissionByRoute(route) {
    const store = useContextStore(),
      IsAdmin = store.IsAdmin,
      {Permission} = store.context;

    // Nếu là admin thì cho pass luôn
    if (!Permission || IsAdmin) {
      return true;
    }

    // Thực hiện check permision
    if (Permission) {
      const paths = route.path.split('/'),
        unactiveBus = Permission.UnActiveBusinessPermission.map(e => e.permission_code.toLowerCase()),
        subSystemRouterKey = paths.length > 0 ? paths[1] : null;

      return !(subSystemRouterKey && unactiveBus.includes(subSystemRouterKey));
    }

    return true;
  }

  /**
   * Xử lý thông báo
   * tbngoc 13.01.2023
   */
  async handleNotification() {
    // Xử lý thông báo
    const notifications = window._notification;
    if (notifications) {
      const dbOptionsAfterLoad = store.state.dbOption.dbOptions;
      // Thông báo thuế suất thay đổi
      const postedDate = await store.getters['context/PostedDate'];
      const taxRateChangeNotification = notifications.TaxRateChange;
      if (
        postedDate &&
        postedDate.getFullYear() > 2022 &&
        taxRateChangeNotification &&
        taxRateChangeNotification.show &&
        dbOptionsAfterLoad &&
        dbOptionsAfterLoad.TaxRateNotification
      ) {
        setTimeout(() => {
          popupUtil.show('TaxRateChangeNotification');
        }, 500);
      }
    }
  }

  /**
   * Hàm sắp xếp phòng ban giống với thứ tự ở danh mục phòng ban
   * dqvinh 16.1.2022
   */
  sortDepartments(arr = [], idField = 'department_id', parentIdField = 'parent_id') {
    if (arr?.length > 0) {
      const dataTree = this.nomalizeDataTree(arr, idField, parentIdField);

      const isTree = true;
      const datax = this.getBodyData(dataTree, isTree);

      // Xử lý nếu số lượng bản ghi lớn hơn max thì sẽ cụp đến level 2
      if (datax.length > MSConstant.DefaultMaxRecord.GridTree) {
        datax.forEach(item => {
          if (item.level > 2) {
            item.isHide = true;
          }

          if (item.level === 2) {
            item.expanded = false;
          }
        });
      }
      return datax;
    }
    return [];
  }

  /**
   * Cập nhật title page khi mở màn hình
   */
  updateTitlePageWhenOpenScreen(screenCode, ref_no = null, prefix = null) {
    try {
      const store = useContextStore();
      const {_cacheTitlePage} = window;
      if (Array.isArray(_cacheTitlePage) && screenCode) {
        let titlePage = this.getScreen(screenCode);
        if (titlePage) {
          const {context} = store;
          const companyName = context.TenantName ? '| ' + context.TenantName : '';
          if (screenCode == 'QuickReport') {
            titlePage = 'Sổ cái | ' + titlePage;
          }
          let fullTitle = `${titlePage} ${companyName} | eHospital`;
          if (ref_no) {
            fullTitle = `${ref_no} | ` + fullTitle;
          }

          prefix && (fullTitle = `${prefix} | ` + fullTitle);

          _cacheTitlePage.push(fullTitle);
          document.title = fullTitle;
        }
      }
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * Cập nhật title page khi đóng màn hình
   */
  updateTitlePageWhenCloseSreen(screenCode) {
    const {_cacheTitlePage} = window;
    if (Array.isArray(_cacheTitlePage) && screenCode) {
      const titlePage = this.getScreen(screenCode);
      const lastIndex = _cacheTitlePage.length - 1;
      // Nếu index cuối cùng chứa title hiện tại của screen
      if (_cacheTitlePage[lastIndex] && _cacheTitlePage[lastIndex].contains(titlePage)) {
        _cacheTitlePage.pop();
        const titlePage = _cacheTitlePage[lastIndex - 1];
        if (titlePage) {
          document.title = titlePage;
        } else {
          document.title = 'eHospital';
        }
      }
    }
  }

  /**
   *
   */
  getScreen(code) {
    if (!code) {
      console.error('Chưa cấu hình screen code tại màn hình');
      throw 'Chưa cấu hình screen code tại màn hình';
    }
    if (code === 'login') {
      return 'Đăng nhập';
    }
    const localize = i18n;
    const screenName = localize.t(`i18nMonitor.ScreenName.${code}`);
    if (!screenName) {
      // return '';
      console.error('Chưa định nghĩa screenName tại i18n');
      throw 'Chưa định nghĩa screenName tại i18n';
    }
    if (screenName.indexOf('i18nMonitor') !== -1) {
      // return '';
      console.error('Chưa định nghĩa screenName tại i18n');
      throw 'Chưa định nghĩa screenName tại i18n';
    }
    return screenName;
  }

  /**
   * Xử lý xem trước báo cáo
   * npcuong 10.04.2023
   */
  printPreviewReport(report, result, api = null) {
    if (api == null) {
      api = reportSettingAPI;
    }
    const previewLink = '';
    const iFramePDF = document.createElement('iframe');
    try {
      document.body.appendChild(iFramePDF);
      iFramePDF.src = previewLink;
      iFramePDF.id = 'reportPreview';
      iFramePDF.onload = () => {
        iFramePDF.contentWindow.print();
        this.unmask();
      };
    } catch (err) {
    } finally {
      this.unmask();
    }
  }

  /**
   * Lấy ngày hạch toán từ local storage
   * tvtoan 17.05.2023
   */
  getPostedDateFromLocalStorage() {
    let postedDate = '';
    const temp = localStorage.getItem('context');
    if (temp && typeof temp === 'string') {
      const state = JSON.parse(temp);
      if (state && state.context && state.context.PostedDate) {
        postedDate = moment(state.context.PostedDate).toDate();
      } else {
        postedDate = new Date();
      }
    }
    return postedDate;
  }

  /**
   * Đếm số lượng bản ghi trùng dữa vào arr attributes trong mảng
   * @param {*} arr
   * @param {*} attributes
   * @returns
   */
  countDuplicates(arr, attributes) {
    const countMap = new Map();

    // Duyệt qua mảng và đếm số lượng xuất hiện của từng cặp giá trị thuộc tính
    for (let i = 0; i < arr.length; i++) {
      const obj = arr[i];
      const key = JSON.stringify(attributes.map(attr => obj[attr]));

      if (countMap.has(key)) {
        countMap.set(key, countMap.get(key) + 1);
      } else {
        countMap.set(key, 1);
      }
    }

    // Tính tổng số lượng object trùng lặp
    let totalCount = 0;
    for (const count of countMap.values()) {
      if (count > 1) {
        totalCount += count;
      }
    }

    return totalCount;
  }

  /**
   * Xử lý lỗi ghi sổ hàng loạt
   * NNLINH 21.07.2022
   * Type = 1: ghi sổ, 2: bỏ ghi
   * tvtoan 6.5.2023 chuyển từ baseVoucherList qua commonFn
   */
  postedMultiError(error, records, type) {
    const me = this;
    const data = {
      gridData: [],
      postedCount: records.length,
      postedErrorCount: error.length,
      postedSuccessCount: records.length - error.length,
      type: type,
    };
    let typeForm = MSConstant.NotificationType.CloseBook;
    if (error.length) {
      // Format lại lỗi của từng bản ghi bị lỗi
      error.forEach(er => {
        // Lỗi hiển thị trên grid
        let errorCode = '';
        let errorCodeArr = [];

        if (
          er.DataBusinessException &&
          er.DataBusinessException.Data &&
          Array.isArray(er.DataBusinessException.Data) &&
          er.DataBusinessException.Data.length > 0
        ) {
          // Danh sách mã hàng hóa đã kiểm tra
          const inventoryCodeArray = [];
          const outwardRefNoArr = [];
          // trong 1 bản ghi ghi sổ có thể có sinh ra lỗi ghi sổ tại nhiều bảng kê
          er.DataBusinessException.Data.forEach(item => {
            if (er.DataBusinessException.Code.includes(';')) {
              // Đổi câu thông báo chung thiếu thông tin hạch toán, lọc lấy 1 lần để ko lặp lại
              errorCode = MSConstant.PostedErrorCode.Post_RequireAccountingInfo;
              if (!errorCodeArr.includes(errorCode)) {
                errorCodeArr.push(errorCode);
              }
            } else {
              // Trường hợp lỗi nhập kho
              if (item.ref_no_outward) {
                // Build câu thông báo gộp 2 bản ghi thành 1 nên gắn lại mảng lỗi = []
                errorCodeArr = [];
                outwardRefNoArr.push(item.ref_no_outward);
              }
              errorCode = this.customErrorPosted(
                er.DataBusinessException.Code,
                item,
                inventoryCodeArray,
                outwardRefNoArr,
              );
              if (errorCode) {
                errorCodeArr.push(errorCode);
              }
              if (item.inventory_item_code) {
                inventoryCodeArray.push(item.inventory_item_code);
              }
            }
          });

          // Lấy bản ghi lỗi đầu lm thông tin master
          const dataErr = er.DataBusinessException.Data[0];
          let errCode = errorCodeArr.filter(x => x).join('');

          if (er.DataBusinessException.Code == 'Post_ValidFixedAssetIncrement') {
            errCode = MSConstant.PostedErrorCode.Message_Post_ValidFixedAssetIncrement.format(dataErr.fixed_asset_code);
          } else if (er.DataBusinessException.Code == 'Post_ValidFixedAssetDecrement') {
            errCode = MSConstant.PostedErrorCode.Message_Post_ValidFixedAssetDecrement.format(dataErr.fixed_asset_code);
          }

          // fomat lại dữ liệu trước khi đẩy vào grid dữ liệu lỗi
          const mess = {
            ref_id: dataErr.ref_id,
            ref_date: dataErr.ref_date,
            ref_type: dataErr.ref_type,
            ref_no_export: dataErr.ref_no,
            ref_no: dataErr.ref_no,
            errorCode: errCode,
            code: er.DataBusinessException.Code,
            status: MSConstant.PostResult.NotSuccess,
          };
          data.gridData.push(mess);
        }

        if (er.error_close_date) {
          typeForm = 2;
          const mess = {
            ref_id: er.ref_id,
            ref_date: er.ref_date,
            ref_type: er.ref_type,
            ref_no: er.ref_no,
            errorCode: 'Ngày chứng từ <= Ngày khóa sổ',
            status: MSConstant.PostResult.NotSuccess,
          };
          data.gridData.push(mess);
        }
      });
      let param = {
        mode: MSConstant.FormState.Add,
        data: data,
        filterRefType: me.filterRefType,
        options: {},
        type: typeForm,
      };
      if (typeForm == 2) {
        param.options = {
          close: () => {
            let title = '';
            const dbOption = useDbOptionStore();
            if (dbOption.state.UseQLTSVN) {
              title = 'Ngắt kết nối không thành công';
            } else {
              title = 'Kết nối không thành công';
            }
            showError(
              'Chứng từ hạch toán tài sản đã lên sổ sách, chương trình không tự động bỏ ghi chứng từ, bạn cần vào lại phân hệ tài sản để xóa bỏ các chứng từ này trước khi thực hiện kết nối',
              title,
              450,
              'Đóng',
            );
          },
        };
      }
      if (type == 3) {
        return param;
      } else {
        // show form danh sách lỗi ghi sổ, bỏ ghi
        popupUtil.show('PostedBatchResult', param);
      }
    }
  }

  /**
   * Khởi tạo check file luồng async
   */
  createIntervalCheckPull(totalCheckCount, timeIntervalPull, fileName, fileDownName, isDownload = false) {
    let count = 0;
    let intervalId = setInterval(async () => {
      count++;
      if (totalCheckCount == count) clearInterval(intervalId);
      try {
        const res = await fileApi.checkTempFile(fileName);
        if (res) {
          let link;

          if (isDownload) {
            link = fileApi.getDownloadLink(StorageFileType.Temp, fileName, fileDownName);
          } else {
            link = fileApi.getFilePreviewLink(StorageFileType.Temp, fileName, fileDownName);
          }

          this.downloadLink(link);
          clearInterval(intervalId);

          //Thêm event bus ẩn toast
          EventBusGlobal.$emit(GlobalEventName.clearToast);

          // thông báo lưu thành công
          useToast().success('Lưu tệp thành công', {position: 'top', duration: 3000});
        }
      } catch (error) {}
    }, timeIntervalPull);
    return intervalId;
  }

  /**
   * Custom lại các mã lỗi đặc biệt
   * NNLINH 18.08.2022
   * tvtoan 27.5.2023 chuyển từ baseVoucherList qua commonFn
   */
  customErrorPosted(errCode, dataErr, inventoryCodeArray, outwardRefNoArr) {
    const me = this;
    let errorCode;
    switch (errCode) {
      // Bảng kê
      case MSConstant.PostedErrorCode.UnPost_ErrorExistInBUVoucherList_Code:
        errorCode = MSConstant.PostedErrorCode.UnPost_ErrorExistInBUVoucherList_Multi.format(dataErr.exception_ref_no);
        break;
      // Lỗi chưa xác định đích danh phiếu nhập
      case MSConstant.PostedErrorCode.Post_ValidConfrontingRefIDForRealNameCode:
        if (!inventoryCodeArray.includes(dataErr.inventory_item_code)) {
          errorCode = MSConstant.PostedErrorCode.Post_ValidConfrontingRefIDForRealName.format(
            dataErr.inventory_item_code,
            dataErr.inventory_item_name,
          );
        }
        break;
      // Lỗi quá số tồn vthh theo số ct đối trừ
      case MSConstant.PostedErrorCode.Post_ValidateOutwardQuantityRealNameCode:
        errorCode = MSConstant.PostedErrorCode.Post_ValidateOutwardQuantityRealName.format(
          dataErr.inventory_item_code,
          dataErr.inventory_item_name,
          dataErr.confronting_ref_no,
          dataErr.quantity_balance,
        );
        break;
      // Lỗi quá số tồn vthh theo kho
      case MSConstant.PostedErrorCode.Post_ValidateOutwardQuantityCode:
        errorCode = MSConstant.PostedErrorCode.Post_ValidateOutwardQuantity.format(
          dataErr.inventory_item_code,
          dataErr.inventory_item_name,
          dataErr.stock_code,
          dataErr.stock_name,
          me.getMoment(dataErr.posted_date).format('DD/MM/YYYY'),
          dataErr.quantity,
        );
        break;
      case MSConstant.PostedErrorCode.UnPost_Warning_OutwardBeUpdated_Code:
        const OutwardRefNo = outwardRefNoArr.filter(x => x).join(', ');
        errorCode = MSConstant.PostedErrorCode.UnPost_Warning_OutwardBeUpdated.format(
          dataErr.inward_ref_no,
          OutwardRefNo,
        );
        break;
      case MSConstant.PostedErrorCode.Post_ValidQuantityToolCode:
        var mes = '';
        if (dataErr.ref_type == MSConstant.RefType.SUDecrement) {
          mes = MSConstant.PostedErrorCode.Post_ValidateToolQuantity_Decrement;
        } else if (dataErr.ref_type == MSConstant.RefType.SUTransfer) {
          mes = MSConstant.PostedErrorCode.Post_ValidateToolQuantity_Tranfer;
        }
        errorCode = mes.format(
          dataErr.inventory_item_code,
          dataErr.inventory_item_name,
          dataErr.department_code,
          dataErr.department_name,
          me.getMoment(dataErr.ref_date).format('DD/MM/YYYY'),
          dataErr.quantity,
        );
        break;
      case MSConstant.PostedErrorCode.Post_ValidAmountToolCode:
        var mes = '';
        if (dataErr.ref_type == MSConstant.RefType.SUDecrement) {
          mes = MSConstant.PostedErrorCode.Post_ValidateToolAmount_Decrement;
        } else if (dataErr.ref_type == MSConstant.RefType.SUTransfer) {
          mes = MSConstant.PostedErrorCode.Post_ValidateToolAmount_Tranfer;
        }
        errorCode = mes.format(
          dataErr.inventory_item_code,
          dataErr.inventory_item_name,
          dataErr.department_code,
          dataErr.department_name,
          me.getMoment(dataErr.ref_date).format('DD/MM/YYYY'),
          msFormat.formatData(dataErr.amount, {
            formatType: MSConstant.FormatType.Amount,
          }),
        );
        break;
      default:
        // Trường hợp các lỗi cơ bản đã được ghi trong file constant PostedErrorCode
        errorCode = MSConstant.PostedErrorCode[errCode];
        break;
    }
    return errorCode;
  }

  /**
   * @description Sắp xếp lại data theo cách sắp xếp của tree
   * @author nmduc1 26.07.2023
   */
  sortAsTree(data, idField, parentField, expanded) {
    const me = this;
    const dataTree = me.nomalizeDataTree(data, idField, parentField);
    let result = me.getBodyData(dataTree, expanded);
    return result;
  }

  /**
   * Lấy dữ liệu combobox data chỉ có năm ( mặc định là năm hạch toán + 15 năm sau)
   * pntoan 9.8.2023
   */
  getComboboxDataOnlyYear() {
    const me = this,
      // Lấy năm hạch toán
      postedDate = me.getPostedDateFromLocalStorage(),
      postedYear = moment(postedDate).year(),
      result = [];

    for (let i = 0; i <= 15; i++) {
      result.push({
        year: `${postedYear + i}`,
      });
    }
    return result;
  }

  /**
   * Hàm format hiển thị chuỗi ngày tháng trên file stimul
   * VDDONG 06/09/2023
   */
  formatDateString(inputString) {
    const parts = inputString.split(' ');

    if (parts.length === 6) {
      const ngay = parts[1].length === 1 ? '0' + parts[1] : parts[1];
      const thang = parts[3].length === 1 ? '0' + parts[3] : parts[3];
      const nam = parts[5];

      return `ngày ${ngay} tháng ${thang} năm ${nam}`;
    }

    return inputString; // Trả về nguyên gốc nếu không phải định dạng mong muốn
  }

  /**
   * Hàm format hiển thị chuỗi ngày tháng trên file stimul
   * TVTOAN 10/05/2023
   */
  formatDateV2(dateInput) {
    const date = dateInput;
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString();
    return `${day}/${month}/${year}`;
  }

  /**
   * Lấy thông tin ngày ký theo dboption  Ngày ... tháng ... năm ...
   */
  getDisplaySingatureDateReport(model) {
    let me = this;
    const dbOption = useDbOptionStore().state;
    const optionDate = dbOption.PrintSystemDate;
    let textDisplay = '';
    // Nếu in trong ngày hiện tại thì lấy
    if (optionDate == 0) {
      const currentDate = new Date();
      let day = currentDate.getDate() > 9 ? currentDate.getDate() : '0' + currentDate.getDate();
      let month = currentDate.getMonth() + 1 > 2 ? currentDate.getMonth() + 1 : '0' + (currentDate.getMonth() + 1);

      textDisplay = `Ngày ${day} tháng ${month} năm ${currentDate.getFullYear()}`;
    }
    // In ngày chứng từ
    else if (optionDate == 1) {
      const refDate = model.ref_date ? moment(model.ref_date).toDate() : new Date();
      let day = refDate.getDate() > 9 ? refDate.getDate() : '0' + refDate.getDate();
      let month = refDate.getMonth() + 1 > 2 ? refDate.getMonth() + 1 : '0' + (refDate.getMonth() + 1);

      textDisplay = `Ngày ${day} tháng ${month} năm ${refDate.getFullYear()}`;
    } else {
      textDisplay = 'Ngày .... tháng .... năm ..... ';
    }
    return {DisplaySignDate: textDisplay};
  }

  /**
   * Lấy ngày dạng Ngày tháng năm
   * pdtai 07.11.2023
   */
  getReadDateVietNamese(date) {
    var textDisplay = '';
    if (date) {
      const currentDate = moment(date).toDate();
      let day = currentDate.getDate() > 9 ? currentDate.getDate() : '0' + currentDate.getDate();
      let month = currentDate.getMonth() + 1 > 2 ? currentDate.getMonth() + 1 : '0' + (currentDate.getMonth() + 1);

      textDisplay = `Ngày ${day} tháng ${month} năm ${currentDate.getFullYear()}`;
    }

    return textDisplay;
  }

  /**
   * Lấy ngày dạng DD/MM/YYYY Ả Rập
   * Ngày < 10 thì thêm số 0 phía trước
   * Tháng < 3 thì thêm số 0 phí trước
   * pdtai 13.11.2023
   */
  getDayMonthYearArabSaudi(date) {
    var textDisplay = '';
    if (date) {
      const currentDate = moment(date).toDate();
      let day = currentDate.getDate() > 9 ? currentDate.getDate() : '0' + currentDate.getDate();
      let month = currentDate.getMonth() + 1 > 2 ? currentDate.getMonth() + 1 : '0' + (currentDate.getMonth() + 1);

      textDisplay = `${day}/${month}/${currentDate.getFullYear()}`;
    }

    return textDisplay;
  }

  /**
   * Hàm lấy thiết lập chân chữ ký
   */
  getSignatureConfig() {
    const me = this;
    const dbOptions = useDbOptionStore().state;
    let config = {
      CommuneAccountantTitle: dbOptions.CommuneAccountantTitle,
      CommuneCashierTitle: dbOptions.CommuneCashierTitle,
      CommuneChiefAccountantTitle: dbOptions?.CommuneChiefAccountantTitle,
      CommuneChiefOfFinanceTitle: dbOptions?.CommuneChiefOfFinanceTitle,
      CommuneDirectorTitle: dbOptions?.CommuneDirectorTitle,
      CommuneReportPreparerTitle: dbOptions?.CommuneReportPreparerTitle,
      CommuneStoreKeeperTitle: dbOptions?.CommuneStoreKeeperTitle,
      PrintSigner: dbOptions?.PrintSigner,
    };
    if (dbOptions.PrintPersonNameInReport) {
      config = {
        ...config,
        CommuneAccountant: dbOptions?.CommuneAccountant,
        CommuneCashier: dbOptions?.CommuneCashier,
        CommuneChiefAccountant: dbOptions?.CommuneChiefAccountant,
        CommuneChiefOfFinance: dbOptions?.CommuneChiefOfFinance,
        CommuneDirector: dbOptions?.CommuneDirector,
        CommuneReportPreparer: dbOptions?.CommuneReportPreparer,
        CommuneStoreKeeper: dbOptions?.CommuneStoreKeeper,
      };
    }
    return config;
  }

  // #endregion
  /**
   * Mua/gia hạn license
   */
  buyLicense(type, showWarningExpiredNoti) {
    let title = type == 1 ? 'Mua ngay' : 'Gia hạn ngay';

    if (type == 1) {
      showAlert('Tính năng đang phát triển', 'Thông báo');
    }
  }

  /**
   * Đóng cảnh báo hết hạn license
   * @param {Number} popupType 1 Top | 2 Center | 3 Bottom-Right
   * nvxuan 21.09.2023
   */
  async closeWarningDate(popupType, tenantId) {
    const contextStore = useContextStore(),
      payload = {
        WarningPopupType: popupType,
        TenantID: tenantId,
      };

    await contextStore.closeWarningDate(payload);
  }

  /**
   * Build câu thông báo lỗi theo object Lỗi truyền vào
   * @param {*} error
   * NMDUC1 26/09.2023
   */
  postedConvertError(error) {
    const me = this;
    let errorCode = '';
    let errorCodeArr = [];
    if (error && error.Data && Array.isArray(error.Data) && error.Data.length > 0) {
      // Danh sách mã hàng hóa đã kiểm tra
      const inventoryCodeArray = [];
      const outwardRefNoArr = [];
      // trong 1 bản ghi ghi sổ có thể có sinh ra lỗi ghi sổ tại nhiều bảng kê
      error.Data.forEach(item => {
        if (error.Code.includes(';')) {
          // Đổi câu thông báo chung thiếu thông tin hạch toán, lọc lấy 1 lần để ko lặp lại
          errorCode = MSConstant.PostedErrorCode.Post_RequireAccountingInfo;
          if (!errorCodeArr.includes(errorCode)) {
            errorCodeArr.push(errorCode);
          }
        } else {
          // Trường hợp lỗi nhập kho
          if (item.ref_no_outward) {
            // Build câu thông báo gộp 2 bản ghi thành 1 nên gắn lại mảng lỗi = []
            errorCodeArr = [];
            outwardRefNoArr.push(item.ref_no_outward);
          }
          errorCode = this.customErrorPosted(error.Code, item, inventoryCodeArray, outwardRefNoArr);
          if (errorCode && !errorCodeArr.includes(errorCode)) {
            errorCodeArr.push(errorCode);
          }
          if (item.inventory_item_code) {
            inventoryCodeArray.push(item.inventory_item_code);
          }
        }
      });

      // Lấy bản ghi lỗi đầu lm thông tin master
      const dataErr = error.Data[0];
      let errPosted = 'Ghi sổ lỗi. ';
      let errCode = '';
      errCode = errPosted + errorCodeArr.filter(x => x).join(', ');

      if (error.Code == 'Post_ValidFixedAssetIncrement') {
        errCode =
          errPosted + MSConstant.PostedErrorCode.Message_Post_ValidFixedAssetIncrement.format(dataErr.fixed_asset_code);
      } else if (error.Code == 'Post_ValidFixedAssetDecrement') {
        errCode =
          errPosted + MSConstant.PostedErrorCode.Message_Post_ValidFixedAssetDecrement.format(dataErr.fixed_asset_code);
      }
      return errCode;
    }
    return '';
  }

  /**
   * Chuyển datetime sang chuỗi tự chọn
   * {date}: ngày tháng
   * {dateFormat}: format muốn chuyển thành
   * VDDONG 03/10/2023
   */
  formatDateCustom(date, dateFormat) {
    date = new Date(Date.parse(date));
    return moment(date).format(dateFormat);
  }

  //#region Nhật ký truy cập
  getCurUserId() {
    return useContextStore().CurUserId;
  }

  /**
   * Lấy tên người dùng
   */
  getCurUserName() {
    return useContextStore()?.context?.user?.full_name;
  }

  //#endregion

  /**
   * @function
   * @description Xóa 1 prop từ Tree
   * @author NXHung 14.12.2023
   * @param {*} node
   * @param {*} propertyToDelete
   */
  deletePropertyFromTree(node, propertyToDelete) {
    const deleteProp = (node, propertyToDelete) => {
      if (node && typeof node === 'object') {
        // Delete the property if it exists
        if (node.hasOwnProperty(propertyToDelete)) {
          delete node[propertyToDelete];
        }

        // Recursively call the function for child nodes
        for (let key in node) {
          if (node.hasOwnProperty(key) && typeof node[key] === 'object') {
            deleteProp(node[key], propertyToDelete);
          }
        }
      }
    };

    deleteProp(node, propertyToDelete);

    return node;
  }

  /**
   * Lấy dữ liệu cho combobox năm theo giá trị truyền vào
   * VDDONG 21/12/2023
   */
  getDataComboboxPlanYearFromTo(fromYear, toYear) {
    let arr = [];
    if (fromYear && toYear) {
      for (let i = fromYear; i <= toYear; i++) {
        arr.push({
          plan_year: i,
          plan_year_str: i.toString(),
        });
      }
    }

    return arr;
  }

  /**
   * @function
   * @description So sánh string bắt đầu trong 1 array
   * @param {*} str
   * @param {*} prefixes
   * @returns
   */
  startsWithAny(str, prefixes) {
    return prefixes.some(prefix => str.startsWith(prefix));
  }

  /**
   * @function
   * @description Xử lý cơ chế gọi lại file
   * @param {*} arrayConfig config cấu hình trong env.js
   * @param {*} callbackCheckSuccessAsync: Gọi liên tục để xem đã file đã được lưu chưa
   * @param {*} callbackHandleAction: Nếu đã thành công, lấy file về
   * @param {*} callbackCheckCancelAsync: Force hủy vòng lặp (Nếu cần)
   * nmduc1 19/02/2024
   * @returns
   */
  intervalPulling(
    arrayConfig,
    callbackCheckSuccessAsync,
    callbackHandleAction,
    callbackCheckCancelAsync = async () => false,
    callbackHandleWhenOutOfPull = () => {},
  ) {
    const me = this;
    let timeCount = 0;
    function createInterval(config, curIndex) {
      const interval = setInterval(async () => {
        timeCount++;

        // kiểm tra hủy interval
        const isCancel = await callbackCheckCancelAsync();
        if (isCancel) {
          clearInterval(interval);
          me.unmask();
          return;
        }

        // kiểm tra thành công
        if (typeof callbackCheckSuccessAsync === 'function' && typeof callbackHandleAction === 'function') {
          const isSuccess = await callbackCheckSuccessAsync();
          if (isSuccess) {
            clearInterval(interval);
            await callbackHandleAction();
            me.unmask();
          }
        }

        //Nếu số lần gọi vượt qua config hiện tại thì sang config tiếp theo
        if (timeCount > config.timeCount) {
          // Check tồn tại config tiếp theo thì sang config đó
          if (curIndex < arrayConfig.length - 1) {
            clearInterval(interval);
            createInterval(arrayConfig[curIndex + 1]);
          }
          // trường hợp quá số lần gọi thì hủy interval, chuyển sang nhận thụ động file từ socket
          else {
            clearInterval(interval);
            callbackHandleWhenOutOfPull();
            me.unmask();
          }
        }
      }, config.timeDelay);

      return interval;
    }

    var res = createInterval(arrayConfig[0], 0);
    return res;
  }

  //#region Báo cáo
  /**
   * Lấy tên popup preview báo cáo theo key
   * @param {String} key Phải nằm trong object cfg của function
   * @returns
   */
  async getReportPreviewPopupName(key, ssid) {
    let cfg = {
        worker: {
          basevoucherlist_printlist: 'DynamicReportPrintPreview',
          printvoucher_print: 'VoucherReportPrintPreview',
          printvoucher_printcustomreport: 'VoucherReportPrintPreview',
          drilldown: 'StaticReportPrintPreview',
          basereportparam_def: 'StaticReportPrintPreview',
          reportlist: 'StaticReportPrintPreview',
          basedictionarylist: 'VoucherReportPrintPreview',
        },
        api: {
          basevoucherlist_printlist: 'DynamicReportPrintPreviewSync',
          printvoucher_print: 'VoucherReportPrintPreviewSync',
          printvoucher_printcustomreport: 'VoucherReportPrintPreviewSync',
          drilldown: 'StaticReportPrintPreviewSync',
          basereportparam_def: 'StaticReportPrintPreviewSync',
          reportlist: 'StaticReportPrintPreviewSync',
          basedictionarylist: 'VoucherReportPrintPreviewSync',
        },
      },
      printMechanism = window._appConfig?.printMechanism ?? 'worker',
      indexDbInstance = new IndexedDB(),
      keyCache = 'lastReportConfig',
      reportCfgs = (await indexDbInstance.get(keyCache)) || [],
      reportCfg = reportCfgs.find(e => e.sessionId == ssid) ?? {};

    if (!key || typeof key != 'string')
      throw 'CommonFunction/getReportPreviewPopupName with key invalid: ' + (key ?? '');

    key = key.toLowerCase();

    reportCfg?.isBigReport && (printMechanism = 'worker');

    if (!cfg[printMechanism][key]) throw 'CommonFunction/getReportPreviewPopupName with key invalid: ' + (key ?? '');

    return cfg[printMechanism][key];
  }

  /**
   * Lấy path API tạo session in
   */
  getSessionPrintPath(param) {
    // Với 1 số báo cáo fix nhiều trang thì cần chuyển sang worker
    const reportId = param?.ReportId ?? param?.report_id,
      pathSync = 'session-print',
      pathAsync = 'session-print-sync';

    if (this.isBigDataReport(reportId)) return pathSync;

    //Nếu là in danh sách chứng từthì sử dụng worker
    if (param?.ReportId == 'print_list_voucher') return pathAsync;

    return window._appConfig?.printMechanism == 'api' ? pathAsync : pathSync;
  }

  /**
   * Là báo cáo dữ liệu lớn
   */
  isBigDataReport(reportId) {
    return false;
  }

  /**
   * Lấy host để thực hiện render báo cáo
   */
  async getHostReportConfig(ssid) {
    let indexDbInstance = new IndexedDB(),
      keyCache = 'lastReportConfig',
      cfgs = (await indexDbInstance.get(keyCache)) || [],
      cfg = cfgs.find(e => e.sessionId == ssid) ?? {},
      hostCfg = cfg?.host;

    switch (hostCfg) {
      case WebSocketEvent.VoucherReportRendered:
        return window._apis.VoucherReportApi;
      case WebSocketEvent.StaticReportRendered:
        return window._apis.StaticReportApi;
      case WebSocketEvent.DynamicReportRendered:
        return window._apis.DynamicReportApi;
      default:
        return null;
    }
  }

  /**
   * saveReportInfoToStorage
   * @param {*} host: WebSocketEvent.StaticReportRendered / WebSocketEvent.VoucherReportRendered / WebSocketEvent.DynamicReportRendered
   * @returns
   */
  async saveReportInfoToStorage(param, result, host) {
    let indexDbInstance = new IndexedDB(),
      keyCache = 'lastReportConfig',
      reportId = param?.ReportId,
      ssid = result.SessionId,
      cfgs = (await indexDbInstance.get(keyCache)) || [],
      cfg = cfgs.find(e => e.sessionId == ssid) ?? {};

    // Lưu tạm thông tin báo cáo vào sessionStorage để khi thực hiện chức năng xuất khẩu file thì có tên báo cáo để dùng
    result?.ReportList?.report_id &&
      result?.ReportList?.report_name &&
      sessionStorage.setItem('ReportName_' + result.ReportList.report_id, result.ReportList.report_name);

    const newConfig = {
      sessionId: ssid,
      reportId,
      host,
      isBigReport: this.isBigDataReport(reportId),
    };
    // Nếu có dữ liệu thì lưu thêm
    result.Data && (newConfig.data = JSON.stringify(result.Data));
    Object.assign(cfg, newConfig);

    cfgs = cfgs.filter(e => e.sessionId != ssid);
    cfgs.push(cfg);

    await indexDbInstance.set(keyCache, cfgs);
  }
  //#endregion
  /***
   * Lấy tài khoản chi tiết tạo đầu tiên
   * @author nnninh 18.03.2024
   */
  handleSelectAccountFirstChilds(pCode, listAccount) {
    let me = this;
    if (listAccount.length == 0 || (pCode == null && pCode == undefined && pCode == '')) {
      return;
    }
    const pNodes = listAccount.filter(e => e.account_number.startsWith(pCode));
    return me.handleSelectAccountChilds(pCode, pNodes);
  }
  /**
   * Lấy tài khoản chi tiết tạo đầu tiên
   * @author nnninh 18.03.2024
   * HQMINH 21.05.2024: Sửa lại sắp xếp k lấy theo created_date nữa: MBO-643
   */
  handleSelectAccountChilds(pCode, pNodes) {
    let me = this;
    if (pNodes.length == 0) {
      return;
    }
    const pId = pNodes.find(e => e.account_number == pCode)?.account_id;
    const child = pNodes.filter(e => e.parent_id == pId);
    if (child.length > 0) {
      // tìm con tạo đàu tiên
      const fisrtCreated = child.sortObject('account_number ASC')[0];
      return me.handleSelectAccountChilds(fisrtCreated?.account_number, pNodes);
    } else {
      return pCode;
    }
  }

  /**
   * convert date to string
   */
  parseDateTimeToString(val, format = 'DD/MM/YYYY') {
    if (!val) return '';
    return msDateUtil.parseDateTimeToString(val, format);
  }

  /**
   * lấy title dựa theo daterange
   * copy từ base report param
   * ntduong2 29.3.2024
   */
  getTitleByDateRange(fromDate_model, toDate_model, isUseAva = false) {
    // Thực hiện build tiêu đề
    const me = this;
    const fromDate = moment(fromDate_model).toDate();
    const month = fromDate.getMonth() + 1;
    const year = fromDate.getFullYear();
    const day = fromDate.getDate();
    let title = '';
    let monthText = month < 3 ? '0' + month : month;
    let checkPeriod = me.getPeriodByDate(
      fromDate_model,
      toDate_model,
      {
        isCheck: true,
        value: fromDate_model,
      },
      'Period',
      null,
    );
    switch (checkPeriod.period) {
      case 45:
      case 1:
      case 2:
      case 3:
      case 4:
      case 5:
      case 6:
      case 7:
      case 8:
      case 9:
      case 10:
      case 11:
      case 12:
      case 50:
      case 55:
        title = 'tháng {0} năm {1}'.format(monthText, year);
        break;
      case 60:
      case 100:
      case 200:
      case 300:
      case 400:
      case 65:
      case 66:
        title = '{0} năm {1}'.format(me.getQuaterByMonth(month), year);
        break;
      case 500:
      case 80:
      case 90:
        title = 'năm {0}'.format(year);
        break;
      default:
        const from = this.getDayMonthYearArabSaudi(fromDate_model);
        const to = this.getDayMonthYearArabSaudi(toDate_model);

        // nếu sử dụng trong ava chatbot thì chỉ lấy text 'đến ngày...'
        if (isUseAva) {
          title = 'đến ngày {1}'.format(from, to);
        } else {
          title = 'từ ngày {0} đến ngày {1}'.format(from, to);
        }
        break;
    }

    //Nếu từ ngày = đến ngày thì set lại title lấy 1 ngày thôi
    if (fromDate_model == toDate_model) {
      title = this.getReadDateVietNamese(fromDate_model);
    }

    return title;
  }

  /**
   * Hàm lấy quý theo tháng
   */
  getQuaterByMonth(month) {
    let quater = '';
    switch (month) {
      case 1:
      case 2:
      case 3:
        quater = 'quý I';
        break;
      case 4:
      case 5:
      case 6:
        quater = 'quý II';
        break;
      case 7:
      case 8:
      case 9:
        quater = 'quý III';
        break;
      case 10:
      case 11:
      case 12:
        quater = 'quý IV';
        break;
      default:
        quater = 'quý I';
        break;
    }
    return quater;
  }

  //#region BroadChannel

  /**
   * Khởi tạo BroadChannel
   */
  initBroadChannel() {
    try {
      const context = useContextStore().context,
        sessionId = context?.Context?.SessionId,
        dbIdRoot = context?.Context?.DatabaseId;

      if (!sessionId) throw 'Not found id to create';

      window._bc && delete window._bc;
      window._bc = new BroadcastChannel('bbo_' + sessionId);

      window._bc.onmessage = async evt => {
        const evtData = evt.data,
          {key, data} = evtData,
          router = useRouter();

        switch (key) {
          // Chuyển sang dữ liệu khác
          case 'ShowNavigateDbPopup':
            dbIdRoot &&
              data?.database_id &&
              dbIdRoot != data?.database_id &&
              showAlert(
                `Bạn đã chuyển qua dữ liệu <b>${this.processXssValue(data?.database_name)}</b>.`,
                'Chuyển dữ liệu',
              ).then(() => {
                location.href = '/budgetplan';
                // router.push('/budgetplan').then(() => { router.go(); });
              });
            break;
          default:
            break;
        }
      };
    } catch (ex) {
      console.warn('Init BroadChannel faild: ' + ex);
    }
  }

  /**
   * Gửi message thông qua BroadChannel
   */
  sendMsgBroadChannel(key, data) {
    try {
      if (!key || key === null || key === undefined) throw 'key is empty or null';

      // Khởi tạo nếu chưa có
      !window._bc && this.initBroadChannel();

      // Bắn message
      window._bc.postMessage({key, data});
    } catch (ex) {
      console.error('Send message by BroadChannel faild: ' + ex);
    }
  }
  //#endregion

  /**
   * nhhoang1 check permission checkPermissionMicrophone
   * @param {*} callbackFnSuccess
   * @param {*} callbackFnFail
   * nhhoang1 12.05.2024
   */
  checkPermissionMicrophone(callbackFnSuccess, callbackFnPrompt, callbackFnFail) {
    navigator.permissions.query({name: 'microphone'}).then(function (permissionStatus) {
      if (permissionStatus.state === 'granted') {
        if (typeof callbackFnSuccess == 'function') {
          callbackFnSuccess();
        }
      } else if (permissionStatus.state === 'prompt') {
        if (typeof callbackFnPrompt == 'function') {
          callbackFnPrompt();
        }
      } else if (typeof callbackFnFail == 'function') {
        callbackFnFail();
      }
    });
  }

  /**
   * hàm xử lý giống groupby sql :D
   * @param {*} array   mảng cần group
   * @param {*} props   group theo các prop nào
   * @returns [[arr1], [arr2], [arr3]] mỗi mảng sẽ gồm các object có props giống nhau
   * tvtoan 30.05.2024
   */
  groupByMultiKey = ({Group: array, By: props}) => {
    let groups = {};
    let i;
    for (i = 0; i < array.length; i++) {
      const arrayRecord = array[i];
      const group = JSON.stringify(this.getGroupedItems(arrayRecord, props));
      groups[group] = groups[group] || [];
      groups[group].push(arrayRecord);
    }
    return Object.keys(groups).map(group => {
      return groups[group];
    });
  };

  /**
   * hàm bổ trở hàm groupByMultiKey
   * tvtoan 30.05.2024
   */
  getGroupedItems = (item, props) => {
    var returnArray = [];
    let i;
    for (i = 0; i < props.length; i++) {
      returnArray.push(item[props[i]]);
    }
    return returnArray;
  };

  /**
   * hàm so sánh 2 đối tượng theo thuộc tính
   * return thuộc tính khác biệt
   * tvtoan 30.05.2024
   */
  compareObject = (x, y, props) => {
    var propDiff = '';
    for (var propertyName in x) {
      if (props.includes(propertyName)) {
        if (x[propertyName] != y[propertyName]) {
          propDiff = propertyName;
          break;
        }
      }
    }
    return propDiff;
  };

  /**
   * Hàm thực hiện xóa các HTML tag cho chuỗi
   * VDDONG 13/06/2024
   */
  removeHTMLTags(str) {
    return str.replace(/<\/?[^>]+(>|$)/g, '');
  }

  /**
   * chuẩn hóa lại version để hiển thị lên giao diện
   * phiên bản release
   */
  normalizeVersionRelease = version => {
    const parts = version?.split('.');

    if (!parts?.length || parts?.length < 2) {
      return version;
    }

    if (parts[1] === '0') {
      return 'R' + parts[0];
    } else {
      return 'R' + parts[0] + '.' + parts[1];
    }
  };

  /**
   * Bắt đầu 1 luồng hướng dẫn sử dụng mới
   */
  startNewBussinesTour = businessTourCode => {
    EventBusGlobal.$emit(GlobalEventName.changedBussinessTour, businessTourCode);
  };

  /**
   * Lock command thực hiện để tránh dup xử lý
   */
  lockCommand = (proxy, command) => {
    const me = proxy;
    if (me._commandLock) {
      clearTimeout(me._commandLockTimeout);
      me.$nextTick(() => {
        me._commandLockTimeout = setTimeout(() => {
          delete me._commandLock;
        }, 1000);
      });

      throw 'Locking';
    }

    me._commandLock = command;
    me.$nextTick(() => {
      me._commandLockTimeout = setTimeout(() => {
        delete me._commandLock;
      }, 1000);
    });
  };

  /**
   * Return object mapping giữa refType và màn hình detail
   * @returns
   */
  refTypeMapperDetailName = () => {
    const result = {};

    for (const [key, val] of Object.entries(MSConstant.RefTypeMapperDetailName)) {
      if (!val.toString().includes(',')) {
        result[val] = key;
      } else {
        val.forEach(refType => {
          result[refType] = key;
        });
      }
    }
    return result;
  };

  /**
   * Khi xoá chứng từ thành công, lấy thông báo thành công khi xóa thành công.
   * Kiểm tra condition để hiển thị chức năng Khôi phục dữ liệu
   */
  getDeleteRestoreSuccessMessage = (entity, store, mapperRefDetailView) => {
    let msg = i18n.t('i18nBaseForm.Message.DeleteSuccess'),
      duration;

    if (!entity)
      return {
        message: msg,
      };

    const restoreRefTypes = [157, 155, 154, 156, 20, 31, 34, 165, 21, 30, 33, 32, 81, 100],
      ref_id = store?._config?.key && entity[store._config.key] ? entity[store._config.key] : entity.ref_id,
      ref_type = entity[store._config.refType] ?? entity.plan_ref_type ?? entity.ref_type;

    !mapperRefDetailView && (mapperRefDetailView = this.refTypeMapperDetailName());

    // Chỉ một số loại chứng từ thì mới hiển thị Khôi phục khi xóa
    if (ref_type && restoreRefTypes.includes(ref_type)) {
      const detailView = mapperRefDetailView[ref_type],
        url = `${window.location.origin}/popup/${detailView}?vdid=${ref_id}`;

      if (!detailView) {
        console.warn('Chưa cấu hình màn hình detail theo ref_type = ' + ref_type);
      } else {
        msg = `${i18n.t(
          'i18nBaseForm.Message.DeleteDataSuccess',
        )} | <a style="color: var(--primary);font-weight: bold;" title="Phục hồi dữ liệu vừa mới xóa" href="${url}" target="_blank">Hoàn tác</a>`;
        duration = 5000;
      }
    }

    let res = {
      message: msg,
    };

    duration && (res.toastOpt = {duration: duration});

    return res;
  };

  /**
   * Link help
   */
  linkHelp(code, type) {
    type = type;
    return window._helpLink[type] + window._misaHelp[code];
  }

  /**
   * chuyển thành ký tự không dấu
   */
  removeAccents(str) {
    return str?.normalize('NFD')?.replace(/[\u0300-\u036f]/g, '');
  }

  /**
   * chuẩn hóa tên sheet file excel
   */
  sanitizeSheetName(sheetName) {
    return sheetName?.replace(/[\\\/\?\*\[\]\:]/g, ' ')?.trim();
  }

  /**
   * xử lý XSS nhưng vẫn giữ nguyên thẻ HTML
   * @param {*} input String
   * @returns String
   */
  sanitizeHTML(input) {
    if (!input) return '';

    // Tách các thẻ HTML ra khỏi nội dung văn bản
    const div = document.createElement('div');
    div.innerHTML = input;
    const elements = div.childNodes;

    let sanitizedString = '';

    elements.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        // Xử lý XSS cho nội dung văn bản
        sanitizedString += this.processXssValue(node.textContent);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Giữ nguyên các thẻ HTML
        sanitizedString += node.outerHTML;
      }
    });

    return sanitizedString;
  }

  /**
   * viết hoa đầu câu
   */
  capitalizeFirstLetter(string) {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
  }
  /**
   * Get branch
   * @returns
   */
  getBranch() {
    return import.meta.env.VITE_BRANCH;
  }

  getAppCodeBranch(){
    return import.meta.env.VITE_BRANCH == 'igov'? 'iGOV': 'iHOS';
  }
  getMiniGettingStarted() {
    return document.body.querySelector('#mgettingstartedmini');
  }
  /**
   * Hiển thị mini getting started
   * vqphong - 22/7/2025
   */
  showMiniGettingStarted() {
    const el = this.getMiniGettingStarted();
    if (el) {
      el.style.display = 'block';
    }
  }
  hideMiniGettingStarted() {
    const el = this.getMiniGettingStarted();
    if (el) {
      el.style.display = 'none';
    }
  }
  /**
   * Lấy app instance
   * @returns {Object} App instance
   */
  getAppInstance() {
    // Tìm app instance từ global scope hoặc window
    if (window.app) {
      return window.app;
    }
    // Fallback: tìm từ document
    const appElement = document.getElementById('app');
    if (appElement && appElement.__vue__) {
      return appElement.__vue__;
    }
    return null;
  }
  updateProgressGettingStarted(progressText) {
    // const app = this.getAppInstance();
    const miniGettingElement = document.getElementById('mgettingstartedmini');
    if (miniGettingElement) {
      const headerProgressTextElement = miniGettingElement.querySelector('#headerProgressText');
      if (headerProgressTextElement) {
        headerProgressTextElement.innerText = progressText;
      }
    }
  }

    /**
   * Đánh dấu getting hợp nhất cho đơn vị igov
   * @returns Boolean
   */
  isMergeTenantIGOV() {
    const contextStore = useContextStore()
    const platformType = contextStore?.$state?.context?.Context?.PlatformType;
    const IsMergerTenant = contextStore?.$state?.context?.Context?.IsMergerTenant
    return platformType === MSConstant.PlatformType.iGOV && IsMergerTenant;
  }
}

export default new CommonFunction();
